import React, { useState, useEffect, Fragment, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Dialog, Transition, Menu, Combobox } from '@headlessui/react';
import api from '../../services/api';
import { PermissionGate } from '../../hooks/useDynamicPermissions';
import { useAuth } from '../../contexts/AuthContext';
import { useTicketPermissions } from '../../hooks/useTicketPermissions';
import { useIsServiceDeskUser } from '../../hooks/useIsServiceDeskUser';
import { ASSIGNMENT_ONLY_LOCK_CLASS } from '../../utils/ticketAssignmentEdit';
import ChangeStatusModal from '../../components/ChangeStatusModal';
import AttachmentTab from '../../components/AttachmentTab';
import ActivityTab from '../../components/ActivityTab';
import RelatedRecordsTab from '../../components/RelatedRecordsTab';
import TabLoadingSpinner from '../../components/TabLoadingSpinner';
import DuplicateChangeModal from '../../components/DuplicateChangeModal';
import CreateServiceRequestFromChangeModal from '../../components/CreateServiceRequestFromChangeModal';
import RecordTimeModal from '../../components/RecordTimeModal';
import AssignmentGroupChangeConfirmModal from '../../components/AssignmentGroupChangeConfirmModal';
import BreachReasonModal from '../../components/BreachReasonModal';
import RichTextEditor from '../../components/RichTextEditor';
import RichTextDisplay from '../../components/RichTextDisplay';
import ResizableTextDisplay from '../../components/ResizableTextDisplay';
import TicketAccessDenied from '../../components/TicketAccessDenied';
import SlaInstancesDisplay, { areAllSlaInstancesTerminal } from '../../components/SlaInstancesDisplay';
import ServiceNowKnownField, { getExternalSystemDisplayName } from '../../components/ServiceNowKnownField';
import DateTimePickerInput from '../../components/DateTimePickerInput';
import {
  impactLabels,
  priorityLabels,
  getDisplayLabel
} from '../../utils/labelMappings';
import { formatDateTimeForInput, datetimeLocalToUTC } from '../../utils/dateHelpers';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import { useServiceNowLinkRefetchInterval } from '../../hooks/useServiceNowLinkRefetchInterval';
import { trackChangeOperation } from '../../utils/appInsights';
import { stripHtml, sanitizeNoteInput } from '../../utils/sanitize';
import { fetchAssignmentGroupMembers } from '../../utils/groupMembers';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  PencilIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon,
  ShieldCheckIcon,
  ClockIcon,
  CheckIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  TicketIcon,
  ChatBubbleLeftRightIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronUpDownIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';

interface WorkNote {
  id: string;
  User?: {
    firstName: string;
    lastName: string;
    email: string;
  };
  note: string;
  timestamp: string;
  isPublic: boolean;
}

interface Approval {
  id: string;
  approverId: string;
  state: string;
  approvalStage?: string;
  approvedAt?: string;
  comments?: string;
  User?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

interface Change {
  id: string;
  number: string;
  shortDescription: string;
  description?: string;
  priority: string;
  state: string;
  changeState?: string;
  category: string;
  ticketType: string;
  changeType?: string;
  risk?: string;
  impact?: string;
  cabRequired?: boolean;
  assignmentGroup?: string;
  assignedTo?: string;
  approvalGroupId?: string;
  plannedStartDate?: string;
  plannedEndDate?: string;
  plannedStart?: string;
  plannedEnd?: string;
  actualStart?: string;
  actualEnd?: string;
  implementationPlan?: string;
  backoutPlan?: string;
  testPlan?: string;
  implementationResults?: string;
  reviewResults?: string;
  closureNotes?: string;
  cancellationReason?: string;
  changeCloseCode?: string;
  maintenanceWindow?: string;
  createdAt: string;
  updatedAt: string;
  User_Case_assignedToIdToUser?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  User_Case_callerIdToUser?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  Customer?: {
    id: string;
    accountName: string;
    accountShortName: string;
    emailAddress: string;
  };
  Engagement?: {
    id: string;
    name: string;
    status: string;
  };
  User_Case_contactIdToUser?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  Resource?: {
    id: string;
    name: string;
    resourceId: string;
    resourceType?: string;
    resourceGroupName?: string;
  };
  otherResourceNames?: string;
  Group_Case_assignmentGroupIdToGroup?: {
    id: string;
    name: string;
    description?: string;
    managerId?: string;
  };
  ParentCase?: {
    id: string;
    number: string;
    shortDescription: string;
    ticketType: string;
  };
  CaseWorkNote?: WorkNote[];
  CaseActivity?: any[];
  workNotes?: WorkNote[];
  approvals?: Approval[];
  approvalGroup?: {
    id: string;
    name: string;
  };
  ReferenceAlert?: {
    id: string;
    number: string;
    shortDescription: string;
    state: string;
    severity?: string;
    priority?: string;
  };
  customerApprovalRequested?: boolean;
  customerApprovalStatus?: string;
  customerApprovalComments?: string;
  customerApprovedOnBehalfOf?: boolean;
  contactId?: string;
  customerId?: string;
  responseBreachReason?: string;
  ongoingBreachReason?: string;
  resolutionBreachReason?: string;
}

const priorityColors: Record<string, string> = {
  'critical_1': 'bg-red-100 text-red-800 border-red-200',
  'high_2': 'bg-orange-100 text-orange-800 border-orange-200',
  'moderate_3': 'bg-yellow-100 text-yellow-800 border-yellow-200',
  'low_4': 'bg-blue-100 text-blue-800 border-blue-200',
  'planning_5': 'bg-gray-100 text-gray-800 border-gray-200',
  // Legacy support
  1: 'bg-red-100 text-red-800 border-red-200',
  2: 'bg-orange-100 text-orange-800 border-orange-200',
  3: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  4: 'bg-blue-100 text-blue-800 border-blue-200',
  5: 'bg-gray-100 text-gray-800 border-gray-200'
};


const riskColors = {
  high: 'bg-red-100 text-red-800',
  medium: 'bg-yellow-100 text-yellow-800',
  low: 'bg-green-100 text-green-800'
};

const changeTypeColors = {
  standard: 'bg-blue-100 text-blue-800',
  normal: 'bg-indigo-100 text-indigo-800',
  emergency: 'bg-red-100 text-red-800'
};

const workflowSteps = [
  { id: 'new', name: 'New', icon: DocumentTextIcon },
  { id: 'assess', name: 'Assess', icon: MagnifyingGlassIcon },
  { id: 'authorize', name: 'Authorize', icon: ShieldCheckIcon },
  { id: 'scheduled', name: 'Scheduled', icon: ClockIcon },
  { id: 'implement', name: 'Implement', icon: ExclamationTriangleIcon },
  { id: 'review', name: 'Review', icon: DocumentTextIcon },
  { id: 'closed', name: 'Closed', icon: CheckIcon }
];

interface ChangeEditForm {
  shortDescription?: string;
  description?: string;
  priority?: number | string;
  changeType?: string;
  risk?: string;
  impact?: string;
  justification?: string;
  riskImpactAnalysis?: string;
  implementationPlan?: string;
  backoutPlan?: string;
  testPlan?: string;
  plannedStart?: string;
  plannedEnd?: string;
  assignmentGroup?: string;
  assignedTo?: string | null;
  customerId?: string;
  engagementId?: string;
  callerId?: string;
  contactId?: string;
  resourceId?: string;
  otherResourceNames?: string;
  approvalGroup?: string;
  cabRequired?: boolean;
}

// Priority calculation based on Impact × Risk matrix (same as ChangeCreate)
const calculatePriorityFromImpactRisk = (impact: string, risk: string): string => {
  // Map risk values to numbers
  const riskMap: { [key: string]: number } = {
    'high': 1,
    'moderate': 2,
    'low': 3
  };
  
  const impactNum = parseInt(impact.split('_')[1]) || 3;
  const riskNum = riskMap[risk] || 3;
  
  if (impactNum === 1 && riskNum === 1) return '1'; // Critical
  if ((impactNum === 1 && riskNum === 2) || (impactNum === 2 && riskNum === 1)) return '2'; // High
  if ((impactNum === 1 && riskNum === 3) || (impactNum === 2 && riskNum === 2) || (impactNum === 3 && riskNum === 1)) return '3'; // Moderate
  if ((impactNum === 2 && riskNum === 3) || (impactNum === 3 && riskNum === 2)) return '4'; // Low
  if (impactNum === 3 && riskNum === 3) return '5'; // Planning
  
  return '3'; // Default to Moderate
};

const ChangeDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const changesListSearch = (location.state as { changesListSearch?: string } | null)?.changesListSearch;

  const navigateBackToChanges = () => {
    if (changesListSearch !== undefined) {
      navigate(`/changes${changesListSearch}`);
    } else {
      navigate('/changes');
    }
  };

  const queryClient = useQueryClient();
  const { user, isContact, isSuperAdmin } = useAuth();
  const isServiceDeskUser = useIsServiceDeskUser();
  const { formatDateTime, preferences } = useDateFormatter();
  const [searchParams] = useSearchParams();
  const hasLoggedView = useRef(false);
  const previousStateRef = useRef<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<ChangeEditForm>({});
  const [newNote, setNewNote] = useState('');
  const [noteType, setNoteType] = useState<'work_note' | 'customer_note'>('work_note');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [backendError, setBackendError] = useState<string>('');
  const [selectedResourceIdValue, setSelectedResourceIdValue] = useState<string>('');
  const [isOtherResource, setIsOtherResource] = useState<boolean>(false);

  // Combobox query states
  const [contactQuery, setContactQuery] = useState('');
  const [resourceQuery, setResourceQuery] = useState('');
  const [assignmentGroupQuery, setAssignmentGroupQuery] = useState('');
  const [assignedToQuery, setAssignedToQuery] = useState('');
  const [approvalGroupQuery, setApprovalGroupQuery] = useState('');
  // Note: authorizeAssignmentGroupQuery, authorizeAssignedToQuery removed - authorize modal no longer used
  const [activeTab, setActiveTab] = useState<'details' | 'planning' | 'schedule' | 'implementation' | 'approval' | 'notes' | 'closure' | 'attachments' | 'activities' | 'related'>('details');
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalModalData, setApprovalModalData] = useState<{decision: 'approved' | 'rejected', comments: string}>({decision: 'approved', comments: ''});
  const [showPeerReviewApprovalModal, setShowPeerReviewApprovalModal] = useState(false);
  const [peerReviewApprovalData, setPeerReviewApprovalData] = useState<{decision: 'approved' | 'rejected', comments: string}>({decision: 'approved', comments: ''});
  const [pendingStateChange, setPendingStateChange] = useState<{
    targetState: string;
    actionLabel: string;
  } | null>(null);
  // Note: showAuthorizeModal, reviewDone, authorizeForm removed - authorize transition is now automatic after CAB approval
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignForm, setAssignForm] = useState({
    assignmentGroup: '',
    assignedTo: ''
  });
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showCabApprovalModal, setShowCabApprovalModal] = useState(false);
  const [cabApprovalNotes, setCabApprovalNotes] = useState('');
  const [showCustomerApprovalModal, setShowCustomerApprovalModal] = useState(false);
  const [customerApprovalMessage, setCustomerApprovalMessage] = useState({ type: '', message: '' });
  const [showCustomerApprovalResponseModal, setShowCustomerApprovalResponseModal] = useState(false);
  const [customerApprovalDecision, setCustomerApprovalDecision] = useState<'approved' | 'rejected'>('approved');
  const [customerApprovalComments, setCustomerApprovalComments] = useState('');

  // Approval section expanded states
  const [expandedApprovalSections, setExpandedApprovalSections] = useState<{
    peerReview: boolean;
    cab: boolean;
    customer: boolean;
  }>({ peerReview: true, cab: true, customer: true });

  // Duplicate and Create Service Request modal states
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [createServiceRequestModalOpen, setCreateServiceRequestModalOpen] = useState(false);

  // Record Time modal state
  const [recordTimeModalOpen, setRecordTimeModalOpen] = useState(false);

  // Close Change modal state
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closeForm, setCloseForm] = useState({
    closeCode: '',
    closureNotes: '',
    actualStartDate: '',
    actualEndDate: ''
  });

  // Re-Initiate modal state
  const [reInitiateModal, setReInitiateModal] = useState<{
    isOpen: boolean;
    plannedStart: string;
    plannedEnd: string;
  }>({ isOpen: false, plannedStart: '', plannedEnd: '' });

  // Breach reason modal state
  const [breachReasonModal, setBreachReasonModal] = useState<{
    isOpen: boolean;
    targetType?: 'RESPONSE' | 'ONGOING' | 'RESOLUTION';
  }>({
    isOpen: false,
    targetType: undefined
  });

  // Assignment group change confirmation modal state
  const [groupChangeConfirmModal, setGroupChangeConfirmModal] = useState<{
    isOpen: boolean;
    newGroupId: string | null;
    newGroupName: string;
    pendingPayload: any;
  }>({
    isOpen: false,
    newGroupId: null,
    newGroupName: '',
    pendingPayload: {}
  });

  const serviceNowLinkRefetchInterval = useServiceNowLinkRefetchInterval(id, 'CHANGE');

  const { data: change, isLoading, error, refetch } = useQuery({
    queryKey: ['change', id],
    queryFn: async () => {
      const response = await api.get(`/changes/${id}`);

      // Handle null changeState by defaulting to 'new'
      if (response.data && response.data.changeState === null) {
        console.warn('changeState is null, defaulting to "new"');
        response.data.changeState = 'new';
      }

      return response.data;
    },
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    staleTime: 0,
    refetchInterval: (query) => {
      const linkPoll = serviceNowLinkRefetchInterval(query);
      if (linkPoll) {
        return linkPoll;
      }
      return 15000;
    },
    placeholderData: (previousData) => previousData, // Keep previous data while refetching
  });

  const { data: workNotes = [], isLoading: isLoadingNotes } = useQuery<WorkNote[]>({
    queryKey: ['case', id, 'notes'],
    queryFn: async () => {
      const response = await api.get(`/cases/${id}/work-notes`);
      return response.data;
    },
    enabled: !!id && activeTab === 'notes',
  });

  // ==================== Logging Helper Functions ====================
  /**
   * Gets the user's display name for logging
   */
  const getUserName = (): string => {
    return user?.email || user?.firstName || 'Unknown';
  };

  /**
   * Gets the change number for logging
   */
  const getChangeNumber = (changeData?: Change | null): string => {
    return changeData?.number || change?.number || id || 'Unknown';
  };

  /**
   * Gets the user role information object for logging
   */
  const getUserRoleInfo = () => {
    if (!user) return undefined;
    return {
      firstName: user.firstName,
      lastName: user.lastName,
      role: user.role,
      roleType: user.roleType,
      roleLevel: user.roleLevel,
      userClass: user.class,
      customerId: user.customerId,
      customerName: user.customerName,
      engagementId: user.engagementId,
      engagementName: user.engagementName,
      isServiceDeskUser: isServiceDeskUser,
    };
  };

  /**
   * Helper function to log change operations with consistent structure
   * @param pageName - The page name for the log
   * @param action - The action being logged
   * @param operations - Additional operation data to include
   * @param changeData - Optional change data (defaults to current change)
   */
  const logChangeAction = (
    pageName: 'Change Details' | 'Change Details - Edit' | 'Change Details - Service Request Created' | 'Change Details - Change Created',
    action: string,
    operations: { [key: string]: string | number | boolean | null | undefined } = {},
    changeData?: Change | null
  ) => {
    if (!user) return;
    
    trackChangeOperation(
      pageName,
      getUserName(),
      {
        action,
        changeNumber: getChangeNumber(changeData),
        ...operations,
      },
      getUserRoleInfo()
    );
  };

  // ==================== End Logging Helper Functions ====================

  // Log "Viewed Change Details" when change is loaded
  useEffect(() => {
    if (change && user && !hasLoggedView.current) {
      hasLoggedView.current = true;
      previousStateRef.current = change.changeState || change.state || null;
      logChangeAction(
        'Change Details',
        'Viewed Change Details',
        {},
        change
      );
    }
  }, [change, user, isServiceDeskUser]);

  // Fetch SLA instances for this change
  const { data: slaInstances } = useQuery({
    queryKey: ['sla-instances', 'CHANGE', id],
    queryFn: async () => {
      const response = await api.get(`/sla-instances/CHANGE/${id}`);
      return response.data;
    },
    enabled: !!id,
    // Stop refetching when all SLAs are in terminal states (BREACHED, ACHIEVED, COMPLETED, CANCELLED)
    refetchInterval: (query) => {
      const data = query.state.data;
      if (areAllSlaInstancesTerminal(data)) {
        return false; // Stop polling
      }
      return 30000; // Continue polling every 30 seconds
    }
  });

  // Get ticket permissions based on backend access control and user role
  const { canEdit, canEditAssignment, isAssignmentOnlyEditMode, canChangeState, canAddWorkNote, canAddAttachments, canAddRelated, canPerformActions, editAccessReason } = useTicketPermissions(
    change,
    'changes'
  );

  // Fetch current assignment group members
  const { data: currentGroupMembersData } = useQuery({
    queryKey: ['group-members', change?.Group_Case_assignmentGroupIdToGroup?.id, change?.customerId || change?.Customer?.id],
    queryFn: async () => {
      const groupId = change?.Group_Case_assignmentGroupIdToGroup?.id;
      if (!groupId) return [];
      try {
        return await fetchAssignmentGroupMembers(
          groupId,
          change?.customerId || change?.Customer?.id || null
        );
      } catch (error) {
        console.error('Error fetching group members:', error);
        return [];
      }
    },
    enabled: !!change?.Group_Case_assignmentGroupIdToGroup?.id && !isEditing
  });

  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const response = await api.get('/users');
      return response.data;
    },
    enabled: isEditing,
  });

  const { data: assignmentGroupsData } = useQuery({
    queryKey: ['assignment-groups'],
    queryFn: async () => {
      const response = await api.get('/groups/assignment-groups');
      return response.data;
    },
    enabled: !isContact() && (isEditing || showAssignModal)
  });

  const { data: approvalGroupsData } = useQuery({
    queryKey: ['approval-groups'],
    queryFn: async () => {
      const response = await api.get('/groups/approval-groups');
      return response.data;
    },
    enabled: isEditing && !isContact()
  });

  const { data: peerReviewGroupsData } = useQuery({
    queryKey: ['peer-review-groups'],
    queryFn: async () => {
      const response = await api.get('/groups/peer-review-groups');
      return response.data;
    },
    enabled: !isContact() && (isEditing || change?.changeState === 'new')
  });


  // Fetch contacts for Contact dropdown - filtered by customer
  const { data: contactsData } = useQuery({
    queryKey: ['contacts', editForm.customerId],
    queryFn: async () => {
      if (!editForm.customerId) {
        return { contacts: [] };
      }
      const response = await api.get(`/contacts/dropdown?customerId=${editForm.customerId}`);
      return response.data;
    },
    enabled: isEditing && !!editForm.customerId,
    staleTime: 0, // Always refetch to ensure fresh data
    refetchOnMount: true // Refetch when edit mode is activated
  });

  // Planned Start/End are editable only after customer approval and transition to Scheduled
  const canEditPlannedDates = change?.changeState === 'scheduled';
  // Contact, Resource, Custom Resources, Short Description, Description remain editable until Scheduled
  const canEditResourceAndCustom = change?.changeState !== 'scheduled';

  // Fetch resources for Resource dropdown - when Resource is editable (before Scheduled)
  const engagementIdForResources = editForm.engagementId || change?.engagementId || change?.Engagement?.id || '';
  const { data: resourcesData } = useQuery({
    queryKey: ['resources', engagementIdForResources],
    queryFn: async () => {
      if (!engagementIdForResources) return [];
      const response = await api.get(`/resources?engagementId=${engagementIdForResources}`);
      return Array.isArray(response.data) ? response.data : (response.data.resources || []);
    },
    enabled: !!canEditResourceAndCustom && !!engagementIdForResources && isEditing
  });

  // Fetch group members for assignment dropdown - only when assignment group is selected in edit form
  const { data: groupMembersData } = useQuery({
    queryKey: ['group-members', editForm.assignmentGroup, change?.customerId || change?.Customer?.id || editForm.customerId],
    queryFn: async () => {
      if (!editForm.assignmentGroup) return [];
      return fetchAssignmentGroupMembers(
        editForm.assignmentGroup,
        change?.customerId || change?.Customer?.id || editForm.customerId || null
      );
    },
    enabled: isEditing && !!editForm.assignmentGroup && !isContact(),
    staleTime: 0, // Always refetch to ensure fresh data
    refetchOnMount: true // Refetch when edit mode is activated
  });

  // Note: authorizeGroupMembersData query removed - authorize modal no longer used

  // Fetch group members for assign modal - only when assignment group is selected
  const { data: assignGroupMembersData } = useQuery({
    queryKey: ['assign-group-members', assignForm.assignmentGroup, change?.customerId || change?.Customer?.id || editForm.customerId],
    queryFn: async () => {
      if (!assignForm.assignmentGroup) return [];
      const members = await fetchAssignmentGroupMembers(
        assignForm.assignmentGroup,
        change?.customerId || change?.Customer?.id || editForm.customerId || null
      );
      // Handle both direct array and wrapped response
      return Array.isArray(members) ? members : ((members as any).members || []);
    },
    enabled: !!assignForm.assignmentGroup && showAssignModal && !isContact()
  });

  // Helper function to check if change state is assess or higher (for showing Approval tab)
  const isChangeStateAssessOrHigher = (changeState?: string) => {
    const assessOrHigherStates = ['assess', 'authorize', 'scheduled', 'implement', 'review', 'closed'];
    return changeState ? assessOrHigherStates.includes(changeState.toLowerCase()) : false;
  };

  // Helper function to check if change state is authorize or higher (for disabling edits)
  const isChangeStateAuthorizeOrHigher = (changeState?: string) => {
    const authorizeOrHigherStates = ['authorize', 'scheduled', 'implement', 'review', 'closed'];
    return changeState ? authorizeOrHigherStates.includes(changeState.toLowerCase()) : false;
  };

  // Fetch approval group members for approvals tab
  const { data: approvalGroupMembersData } = useQuery({
    queryKey: ['approval-group-members', change?.approvalGroupId],
    queryFn: async () => {
      if (!change?.approvalGroupId) return [];
      const response = await api.get(`/groups/${change.approvalGroupId}/members`);
      return response.data;
    },
    enabled: !!change?.approvalGroupId && !isContact() && (activeTab === 'approval' || change?.changeState === 'authorize')
  });

  // Fetch peer review workflow status
  const { data: peerReviewStatus, refetch: refetchPeerReviewStatus } = useQuery({
    queryKey: ['peer-review-status', id],
    queryFn: async () => {
      const response = await api.get(`/changes/${id}/approval-workflow/status`);
      const data = response.data;
      // Transform the response to flatten peer review members for easy access in UI
      return {
        currentStage: data.currentStage,
        stages: data.stages,
        peerReviewMembers: data.stages?.peer_review?.members || [],
        cabMembers: data.stages?.cab?.members || [],
        customerMembers: data.stages?.customer?.members || [],
      };
    },
    enabled: !!id && !isContact() && activeTab === 'approval' && ['assess', 'authorize', 'scheduled', 'implement', 'review', 'closed'].includes(change?.changeState || ''),
    refetchInterval: 10000, // Refetch every 10 seconds to update approval status
  });



  const updateChangeMutation = useMutation({
    mutationFn: async (data: Partial<Change>) => {
      const response = await api.put(`/changes/${id}`, data);
      return response.data;
    },
    onSuccess: async (updated: any) => {
      // Update cache with new data to prevent error when access is lost
      queryClient.setQueryData(['change', id], updated);

      // Check if this was a confirmed assignment group change - if so, redirect to list
      if (groupChangeConfirmModal.isOpen) {
        setGroupChangeConfirmModal({
          isOpen: false,
          newGroupId: null,
          newGroupName: '',
          pendingPayload: {}
        });
        toast.success('Change updated successfully. You no longer have access to this change.');
        queryClient.invalidateQueries({ queryKey: ['changes'] });
        queryClient.invalidateQueries({ queryKey: ['cases'] });
        navigateBackToChanges();
        return;
      }

      // Check if user lost access after the update
      if (updated.canEdit === false) {
        toast('Access to this change has changed.');
        navigateBackToChanges();
        return;
      }

      // Log tab-based actions
      if (user) {
        let action = '';
        const changeNumber = updated.number || change?.number || id || 'Unknown';
        
        // Determine action based on active tab
        if (activeTab === 'details') {
          action = 'Change Details Modified';
        } else if (activeTab === 'schedule') {
          action = 'Change Schedule Modified';
        } else if (activeTab === 'closure') {
          action = 'Change Closure Modified';
        } else if (activeTab === 'attachments') {
          action = 'Added Attachments';
        } else if (activeTab === 'related') {
          action = 'Added Related Record';
        }
        
        // Check if assignment changed
        const oldAssignmentGroupId = change?.assignmentGroupId || change?.Group_Case_assignmentGroupIdToGroup?.id;
        const newAssignmentGroupId = updated.assignmentGroupId || updated.Group_Case_assignmentGroupIdToGroup?.id;
        const oldAssignedToId = change?.assignedToId || change?.User_Case_assignedToIdToUser?.id;
        const newAssignedToId = updated.assignedToId || updated.User_Case_assignedToIdToUser?.id;
        
        if ((oldAssignmentGroupId !== newAssignmentGroupId) || (oldAssignedToId !== newAssignedToId)) {
          logChangeAction(
            'Change Details - Edit',
            'Change Assigned',
            {
              assignedGroupId: newAssignmentGroupId || '',
              assignedToId: newAssignedToId || '',
            },
            updated
          );
        } else if (action) {
          // Log tab-based action
          logChangeAction(
            'Change Details - Edit',
            action
          );
        }
      }

      await refetch();
      queryClient.invalidateQueries({ queryKey: ['changes'] });
      queryClient.invalidateQueries({ queryKey: ['case-activities', id] });
      setIsEditing(false);
      setErrors({});
      setBackendError('');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to update change';
      setBackendError(message);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  });

  const addNoteMutation = useMutation({
    mutationFn: async (data: { note: string; isPublic: boolean; noteType?: 'work_note' | 'customer_note' }) => {
      const response = await api.post(`/changes/${id}/work-notes`, {
        note: data.note,
        isPublic: data.isPublic
      });
      return response.data;
    },
    onSuccess: async (responseData, variables) => {
      await refetch();
      queryClient.invalidateQueries({ queryKey: ['case', id, 'notes'] });
      queryClient.invalidateQueries({ queryKey: ['case-activities', id] });
      
      // Determine note type for logging from variables (captured at mutation time)
      const noteTypeForLog = variables.noteType || noteType; // Fallback to state if not in variables
      const noteTypeLabel = noteTypeForLog === 'work_note' ? 'Work Note' : 'Customer Note';
      
      // Log note added with type
      logChangeAction(
        'Change Details - Edit',
        `Note Added - ${noteTypeLabel}`
      );
      
      setNewNote('');
    }
  });

  const approvalMutation = useMutation({
    mutationFn: async (data: { decision: 'approved' | 'rejected'; comments?: string }) => {
      const response = await api.post(`/changes/${id}/approval`, data);
      return response.data;
    },
    onSuccess: async (responseData, variables) => {
      await refetch();
      await refetchPeerReviewStatus();
      queryClient.invalidateQueries({ queryKey: ['changes'] });
      queryClient.invalidateQueries({ queryKey: ['peer-review-status', id] });
      
      // Log approval/rejection action
      const action = variables.decision === 'approved' ? 'Change Approved' : 'Change Rejected';
      logChangeAction(
        'Change Details - Edit',
        action
      );
      // Backend handles all state changes, so we just need to refresh the data
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to process approval';
      setErrors({ approval: message });
    }
  });

  // Peer review response mutation
  const peerReviewMutation = useMutation({
    mutationFn: async (data: { decision: 'approved' | 'rejected'; comments?: string }) => {
      const response = await api.post(`/changes/${id}/peer-review/respond`, data);
      return response.data;
    },
    onSuccess: async (responseData, variables) => {
      await refetch();
      await refetchPeerReviewStatus();
      queryClient.invalidateQueries({ queryKey: ['changes'] });
      queryClient.invalidateQueries({ queryKey: ['peer-review-status', id] });
      setShowPeerReviewApprovalModal(false);
      setPeerReviewApprovalData({ decision: 'approved', comments: '' });
      toast.success('Peer review response submitted successfully');
      
      // Log peer review approval/rejection action
      const action = variables.decision === 'approved' ? 'Change Approved' : 'Change Rejected';
      logChangeAction(
        'Change Details - Edit',
        action
      );
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to process peer review response';
      toast.error(message);
    }
  });

  // Customer approval response mutation
  const customerApprovalMutation = useMutation({
    mutationFn: async (data: { decision: 'approved' | 'rejected', comments: string }) => {
      const response = await api.post(`/changes/${id}/customer-approval/respond`, {
        decision: data.decision,
        comments: data.comments
      });
      return response.data;
    },
    onSuccess: async (responseData, variables) => {
      setIsEditing(false);
      await refetch();
      await refetchPeerReviewStatus();
      queryClient.invalidateQueries({ queryKey: ['changes'] });
      queryClient.invalidateQueries({ queryKey: ['peer-review-status', id] });
      setShowCustomerApprovalResponseModal(false);
      setCustomerApprovalComments('');
      
      // Log customer approval/rejection action
      const action = variables.decision === 'approved' ? 'Change Approved' : 'Change Rejected';
      logChangeAction(
        'Change Details - Edit',
        action
      );
    },
    onError: (error: any) => {
      alert(error.response?.data?.message || 'Failed to process customer approval');
    }
  });

  const handleApproval = (event: React.MouseEvent, decision: 'approved' | 'rejected', comments?: string) => {
    event.preventDefault();
    event.stopPropagation();
    approvalMutation.mutate({ decision, comments });
  };

  const handleRejectionSubmit = () => {
    if (!rejectionReason.trim()) {
      setErrors({ rejection: 'Please provide a reason for rejection' });
      return;
    }
    setShowRejectionModal(false);
    // Create a synthetic event for handleApproval
    const syntheticEvent = {
      preventDefault: () => {},
      stopPropagation: () => {}
    } as React.MouseEvent;
    handleApproval(syntheticEvent, 'rejected', rejectionReason);
    setRejectionReason('');
  };

  const handleCabApprovalSubmit = () => {
    if (!cabApprovalNotes.trim()) {
      setErrors({ approval: 'Please provide approval notes' });
      return;
    }
    setShowCabApprovalModal(false);
    // Create a synthetic event for handleApproval
    const syntheticEvent = {
      preventDefault: () => {},
      stopPropagation: () => {}
    } as React.MouseEvent;
    handleApproval(syntheticEvent, 'approved', cabApprovalNotes);
    setCabApprovalNotes('');
  };

  const handleSendCustomerApproval = async (resend: boolean = false) => {
    try {
      const url = resend
        ? `/changes/${id}/customer-approval/request?resend=true`
        : `/changes/${id}/customer-approval/request`;
      await api.post(url);
      queryClient.invalidateQueries({ queryKey: ['change', id] });
      setCustomerApprovalMessage({
        type: 'success',
        message: resend
          ? 'Approval request resent to customer successfully'
          : 'Approval request sent to customer successfully'
      });
      setShowCustomerApprovalModal(true);
    } catch (error: any) {
      setCustomerApprovalMessage({
        type: 'error',
        message: error.response?.data?.message || 'Failed to send approval request to customer'
      });
      setShowCustomerApprovalModal(true);
    }
  };

  const updateStateMutation = useMutation({
    mutationFn: async (data: {
      changeState: string;
      justification?: string;
      implementationPlan?: string;
      riskImpactAnalysis?: string;
      backoutPlan?: string;
      testPlan?: string;
    }) => {
      const response = await api.patch(`/changes/${id}/state`, data);
      return response.data;
    },
    onSuccess: async () => {
      // Refetch the change data without showing loading state
      await refetch();
      // Invalidate the changes list to refresh the list view
      await queryClient.invalidateQueries({ queryKey: ['changes'] });
    }
  });

  // Re-Initiate mutation
  const reInitiateMutation = useMutation({
    mutationFn: async (data: { plannedStart?: string; plannedEnd?: string }) => {
      const response = await api.post(`/changes/${id}/re-initiate`, data);
      return response.data;
    },
    onSuccess: async () => {
      toast.success('Change has been re-initiated');
      await refetch();
      await queryClient.invalidateQueries({ queryKey: ['changes'] });
      setReInitiateModal({ isOpen: false, plannedStart: '', plannedEnd: '' });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to re-initiate change');
    }
  });

  // Self-assign mutation
  const assignToMeMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(`/changes/${id}/assign-to-me`);
      return response.data;
    },
    onSuccess: async (updatedChange) => {
      toast.success('Change assigned to you successfully');
      queryClient.setQueryData(['change', id], updatedChange);
      await refetch();
      await queryClient.invalidateQueries({ queryKey: ['changes'] });
      await queryClient.invalidateQueries({ queryKey: ['case-activities', id] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to assign change to yourself');
    }
  });

  // Close change mutation
  const closeChangeMutation = useMutation({
    mutationFn: async (data: { changeCloseCode: string; closureNotes: string; actualStartDate: string; actualEndDate: string }) => {
      const response = await api.patch(`/changes/${id}/close`, data);
      return response.data;
    },
    onSuccess: async () => {
      toast.success('Change closed successfully');
      setShowCloseModal(false);
      setCloseForm({ closeCode: '', closureNotes: '', actualStartDate: '', actualEndDate: '' });
      await refetch();
      await queryClient.invalidateQueries({ queryKey: ['changes'] });
      await queryClient.invalidateQueries({ queryKey: ['case-activities', id] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to close change');
    }
  });

  // Breach reason mutation
  const breachReasonMutation = useMutation({
    mutationFn: async ({ breachReason, targetType }: { breachReason: string; targetType: 'RESPONSE' | 'ONGOING' | 'RESOLUTION' }) => {
      const response = await api.put(`/cases/${id}/breach-reason`, { breachReason, targetType });
      return response.data;
    },
    onSuccess: async () => {
      // Refetch data first to ensure UI shows updated breach reasons
      await refetch();
      // Refetch list queries so breach reason shows without browser refresh
      queryClient.refetchQueries({ queryKey: ['changes'] });
      queryClient.refetchQueries({ queryKey: ['cases'] });
      queryClient.invalidateQueries({ queryKey: ['case-activities', id] });
      toast.success('Breach reason saved successfully');
      setBreachReasonModal({ isOpen: false, targetType: undefined });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to save breach reason');
    }
  });

  // Computed values for SLA breach
  const hasSlaBreached = (change as any)?.slaBreached || slaInstances?.some((sla: any) => sla.isBreached || sla.currentState === 'BREACHED') || false;

  // Check if breach reasons are provided for all breached SLAs
  const breachReasons = {
    response: change?.responseBreachReason,
    ongoing: change?.ongoingBreachReason,
    resolution: change?.resolutionBreachReason
  };

  // Helper to check if a specific SLA target type is breached
  const isSlaTargetBreached = (targetType: string): boolean => {
    if (!slaInstances) return false;
    return slaInstances.some(
      (instance: any) =>
        // Check both instance.targetType and instance.SlaPolicy.targetType for compatibility
        (instance.targetType === targetType || instance.SlaPolicy?.targetType === targetType) &&
        (instance.currentState === 'BREACHED' || instance.isBreached)
    );
  };

  // Check if all breached SLAs have breach reasons
  const hasAllBreachReasons = (): boolean => {
    if (!hasSlaBreached) return true;

    // Check each breached SLA target type for its breach reason
    const responseBreached = isSlaTargetBreached('RESPONSE');
    const ongoingBreached = isSlaTargetBreached('ONGOING');
    const resolutionBreached = isSlaTargetBreached('RESOLUTION');

    if (responseBreached && !change?.responseBreachReason) return false;
    if (ongoingBreached && !change?.ongoingBreachReason) return false;
    if (resolutionBreached && !change?.resolutionBreachReason) return false;

    return true;
  };

  // Legacy check for backward compatibility
  const hasBreachReason = hasAllBreachReasons();

  // Handler for Assign To Me button
  const handleAssignToMe = () => {
    if (user?.id) {
      assignToMeMutation.mutate();
    }
  };

  // Check if Assign To Me button should be shown
  const shouldShowAssignToMeButton = () => {
    if (!user?.id || !change || isEditing) return false;
    const currentAssignedToId = change.assignedTo || change.User_Case_assignedToIdToUser?.id || (change.User_Case_assignedToIdToUser as any)?._id;
    if (currentAssignedToId === user.id) return false;
    const isUserInGroup = (currentGroupMembersData || []).some((member: any) =>
      member.id === user.id || member.userId === user.id || member._id === user.id
    );
    // Show button if user is in the group (don't check canEdit - users in the group can assign to themselves)
    return isUserInGroup;
  };

  // Check for edit query parameter
  useEffect(() => {
    if (searchParams.get('edit') === 'true' && change && !isEditing) {
      // We need to defer this until after the component has rendered
      const timer = setTimeout(() => {
        setIsEditing(true);
        setEditForm({
          shortDescription: change.shortDescription,
          priority: change.priority,
          changeType: change.changeType,
          risk: change.risk,
          impact: change.impact,
          implementationPlan: change.implementationPlan,
          backoutPlan: change.backoutPlan,
          testPlan: change.testPlan,
          plannedStart: formatDateTimeForInput(change.plannedStart),
          plannedEnd: formatDateTimeForInput(change.plannedEnd),
          assignmentGroup: change.assignmentGroup || change.Group_Case_assignmentGroupIdToGroup?.id || '',
          assignedTo: change.assignedTo || change.User_Case_assignedToIdToUser?.id || null,
          customerId: change.customerId || change.Customer?.id || '',
          engagementId: change.engagementId || change.Engagement?.id || '',
          callerId: change.callerId || change.User_Case_callerIdToUser?.id || '',
          resourceId: change.resourceId || change.Resource?.id || '',
          approvalGroup: change.approvalGroupId || change.Group_Case_approvalGroupIdToGroup?.id || '',
          cabRequired: change.cabRequired || false
        });
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [searchParams, change, isEditing]);

  // Debug logging to help identify data structure issues
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    // Trigger re-render when approvals, approval group members, or user changes
  }, [change?.approvals, approvalGroupMembersData, user]);

  // Reset active tab to 'details' when navigating to a different Change (e.g., after duplicating)
  useEffect(() => {
    setActiveTab('details');
  }, [id]);

  // Exit edit mode when user navigates away from the Details tab so returning shows View Only
  useEffect(() => {
    if (activeTab !== 'details') {
      setIsEditing(false);
    }
  }, [activeTab]);

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-32 bg-gray-200 rounded"></div>
            <div className="h-48 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !change) {
    return (
      <TicketAccessDenied
        ticketType="Change"
        ticketNumber={change?.number}
        listPageUrl={
          changesListSearch !== undefined ? `/changes${changesListSearch}` : '/changes'
        }
      />
    );
  }

  const handleEdit = () => {
    setEditForm({
      shortDescription: change.shortDescription,
      description: change.description || '',
      priority: change.priority,
      changeType: change.changeType || 'normal', // Default to 'normal' if not set
      risk: change.risk || 'low', // Default to 'low' if not set
      impact: change.impact || 'low_3', // Default to 'low_3' if not set
      justification: change.justification,
      riskImpactAnalysis: change.riskImpactAnalysis,
      implementationPlan: change.implementationPlan,
      backoutPlan: change.backoutPlan,
      testPlan: change.testPlan,
      plannedStart: formatDateTimeForInput(change.plannedStart),
      plannedEnd: formatDateTimeForInput(change.plannedEnd),
      assignmentGroup: change.assignmentGroup || change.Group_Case_assignmentGroupIdToGroup?.id || '',
      assignedTo: change.assignedTo || change.User_Case_assignedToIdToUser?.id || null,
      customerId: change.customerId || change.Customer?.id || '',
      engagementId: change.engagementId || change.Engagement?.id || '',
      callerId: change.callerId || change.User_Case_callerIdToUser?.id || '',
      contactId: change.contactId || change.User_Case_contactIdToUser?.id || '',
      resourceId: change.resourceId || change.Resource?.id || '',
      otherResourceNames: change.otherResourceNames || '',
      approvalGroup: change.approvalGroupId || change.Group_Case_approvalGroupIdToGroup?.id || '',
      cabRequired: change.cabRequired || false
    });
    // Initialize "Others" resource state
    const hasOtherResource = !change.resourceId && !!change.otherResourceNames;
    setIsOtherResource(hasOtherResource);
    setSelectedResourceIdValue(change.Resource?.resourceId || '');
    setIsEditing(true);
  };

  const handleSave = () => {
    if (isAssignmentOnlyEditMode) {
      if (!editForm.assignmentGroup) {
        setErrors({ assignmentGroup: 'Assignment Group is required' });
        return;
      }

      setErrors({});
      updateChangeMutation.mutate({
        assignmentGroupId: editForm.assignmentGroup,
        assignedToId: editForm.assignedTo || null,
      } as Partial<Change>);
      return;
    }

    // Validate required fields (same as Create page)
    const newErrors: Record<string, string> = {};

    if (!editForm.shortDescription?.trim()) {
      newErrors.shortDescription = 'Short description is required';
    }
    if (!editForm.changeType) {
      newErrors.changeType = 'Change type is required';
    }
    if (!editForm.risk) {
      newErrors.risk = 'Risk assessment is required';
    }
    if (!editForm.impact) {
      newErrors.impact = 'Impact is required';
    }
    if (!editForm.customerId) {
      newErrors.customerId = 'Customer is required';
    }
    if (!editForm.engagementId) {
      newErrors.engagementId = 'Engagement is required';
    }
    // Resource validation when editable (before Scheduled)
    if (canEditResourceAndCustom && editForm.engagementId) {
      if (isOtherResource) {
        if (!editForm.otherResourceNames?.trim()) {
          newErrors.otherResourceNames = 'Please enter at least one resource name';
        }
      } else if (!editForm.resourceId) {
        newErrors.resourceId = 'Resource is required (select a resource or choose "Others")';
      }
    }
    // Approval Group is required for Normal and Emergency change types
    if ((editForm.changeType === 'normal' || editForm.changeType === 'emergency') && !editForm.approvalGroup) {
      newErrors.approvalGroup = 'Approval Group is required for Normal and Emergency changes';
    }
    if (!editForm.justification?.trim()) {
      newErrors.justification = 'Justification is required';
    }
    if (!editForm.riskImpactAnalysis?.trim()) {
      newErrors.riskImpactAnalysis = 'Risk & Impact Analysis is required';
    }
    if (!editForm.implementationPlan?.trim()) {
      newErrors.implementationPlan = 'Implementation Plan is required';
    }
    if (!editForm.backoutPlan?.trim()) {
      newErrors.backoutPlan = 'Backout Plan is required';
    }
    if (!editForm.testPlan?.trim()) {
      newErrors.testPlan = 'Test Plan is required';
    }
    // Planned Start/End required and validated only when editable (Scheduled state)
    if (canEditPlannedDates) {
      if (!editForm.plannedStart) {
        newErrors.plannedStart = 'Planned start date is required';
      }
      if (!editForm.plannedEnd) {
        newErrors.plannedEnd = 'Planned end date is required';
      }
      if (editForm.plannedStart && editForm.plannedEnd) {
        const startDate = new Date(editForm.plannedStart);
        const endDate = new Date(editForm.plannedEnd);
        if (endDate <= startDate) {
          newErrors.plannedEnd = 'Planned end date must be after planned start date';
        }
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      console.error('Validation errors:', newErrors);
      return;
    }

    // Clear any previous errors
    setErrors({});
    setBackendError('');

    // Calculate priority based on current risk and impact values
    const calculatedPriority = calculatePriorityFromImpactRisk(
      editForm.impact || 'low_3',
      editForm.risk || 'low'
    );

    const payload: any = {
      shortDescription: editForm.shortDescription,
      description: editForm.description,
      changeType: editForm.changeType,
      risk: editForm.risk,
      impact: editForm.impact,
      priority: calculatedPriority,
      category: 'change',
      urgency: editForm.impact, // Map impact to urgency for compatibility
      justification: editForm.justification,
      riskImpactAnalysis: editForm.riskImpactAnalysis,
      implementationPlan: editForm.implementationPlan,
      backoutPlan: editForm.backoutPlan,
      testPlan: editForm.testPlan,
      plannedStartDate: (() => {
        const raw = canEditPlannedDates ? editForm.plannedStart : (editForm.plannedStart || (change?.plannedStart ? formatDateTimeForInput(change.plannedStart) : null));
        return raw ? datetimeLocalToUTC(raw, preferences.timezone) : null;
      })(),
      plannedEndDate: (() => {
        const raw = canEditPlannedDates ? editForm.plannedEnd : (editForm.plannedEnd || (change?.plannedEnd ? formatDateTimeForInput(change.plannedEnd) : null));
        return raw ? datetimeLocalToUTC(raw, preferences.timezone) : null;
      })(),
      cabRequired: editForm.cabRequired
    };

    // Add optional fields if provided
    if (editForm.assignmentGroup) payload.assignmentGroupId = editForm.assignmentGroup;
    if (editForm.assignedTo) payload.assignedToId = editForm.assignedTo;
    if (editForm.customerId) payload.customerId = editForm.customerId;
    if (editForm.engagementId) payload.engagementId = editForm.engagementId;
    if (editForm.callerId) payload.callerId = editForm.callerId;
    if (editForm.contactId) payload.contactId = editForm.contactId;
    // Resource and Custom Resources - include when editable (before Scheduled)
    if (canEditResourceAndCustom) {
      if (isOtherResource && editForm.otherResourceNames?.trim()) {
        payload.otherResourceNames = editForm.otherResourceNames.trim();
        payload.resourceId = null;
      } else if (editForm.resourceId) {
        payload.resourceId = editForm.resourceId;
        payload.otherResourceNames = null;
      }
    }
    if (editForm.approvalGroup) payload.approvalGroupId = editForm.approvalGroup;

    // Check if assignment group is changing and user is not a member of the new group
    // Only check for regular users (user/agent roles), not for super_admin/admin/manager
    const userRole = user?.role;
    const isGlobalAccessRole = userRole === 'super_admin' || userRole === 'admin' || userRole === 'manager';

    const originalAssignmentGroupId = change.assignmentGroup || change.Group_Case_assignmentGroupIdToGroup?.id;
    if (payload.assignmentGroupId && payload.assignmentGroupId !== originalAssignmentGroupId && !isGlobalAccessRole && !isServiceDeskUser) {
      const newGroupId = payload.assignmentGroupId;
      // Check if user is a member of the new group by checking group members
      // groupMembersData is already fetched for the selected assignment group
      const newGroupMembers = groupMembersData || [];
      const isUserMemberOfNewGroup = newGroupMembers.some((member: any) =>
        member.id === user?.id || member._id === user?.id
      );

      if (!isUserMemberOfNewGroup) {
        // Find the new group name
        const newGroup = (assignmentGroupsData || []).find((group: any) =>
          group.id === newGroupId || group._id === newGroupId
        );
        const newGroupName = newGroup?.name || 'Unknown Group';

        // Show confirmation modal instead of saving immediately
        setGroupChangeConfirmModal({
          isOpen: true,
          newGroupId,
          newGroupName,
          pendingPayload: payload
        });
        return;
      }
    }

    updateChangeMutation.mutate(payload);
  };

  // Handler for confirming assignment group change
  const handleConfirmGroupChange = async () => {
    const payload = groupChangeConfirmModal.pendingPayload;

    // Check if this is an assess state change (has newState property)
    if (payload.newState) {
      try {
        // Make API call to update state and assignments
        await api.patch(`/changes/${id}/state`, payload);

        // Close modal
        setGroupChangeConfirmModal({
          isOpen: false,
          newGroupId: null,
          newGroupName: '',
          pendingPayload: {}
        });

        // Show success message and redirect
        toast.success('Change status updated successfully. You no longer have access to this change.');
        queryClient.invalidateQueries({ queryKey: ['changes'] });
        navigateBackToChanges();
      } catch (error: any) {
        console.error('Failed to update change status:', error);
        toast.error(error.response?.data?.message || 'Failed to update change status');

        // Close modal on error
        setGroupChangeConfirmModal({
          isOpen: false,
          newGroupId: null,
          newGroupName: '',
          pendingPayload: {}
        });
      }
    } else {
      // This is a regular update (not state change), use the existing mutation
      updateChangeMutation.mutate(payload);
      // Note: Navigation to list will happen in mutation's onSuccess
    }
  };

  // Handler for cancelling assignment group change
  const handleCancelGroupChange = () => {
    setGroupChangeConfirmModal({
      isOpen: false,
      newGroupId: null,
      newGroupName: '',
      pendingPayload: {}
    });
  };

  const handleAddNote = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newNote.trim()) return;

    // Capture noteType at the time of mutation to ensure accurate logging
    const currentNoteType = noteType;
    
    addNoteMutation.mutate({
      note: sanitizeNoteInput(newNote),
      isPublic: isContact() ? true : (currentNoteType === 'customer_note'),  // Customer users always create public notes
      noteType: currentNoteType // Pass noteType for logging
    });
  };

  const handleStateChange = async (newState: string) => {
    const getStateLabel = (state: string) => {
      const labels: Record<string, string> = {
        new: 'New',
        assess: 'Assess',
        authorize: 'Authorize',
        scheduled: 'Scheduled',
        implement: 'Implement',
        review: 'Review',
        closed: 'Closed',
        cancelled: 'Cancelled'
      };
      return labels[state] || state;
    };

    // Validate that Approval Group is set if CAB is required before progressing beyond 'new' state
    // Allow transitions to 'new' and 'cancelled' states without this validation
    if (newState !== 'new' && newState !== 'cancelled' && change.cabRequired && !change.approvalGroupId) {
      toast.error(
        'Cannot change state: Approval Group is required for this change. Please edit the change and select an Approval Group before proceeding.',
        { duration: 5000 }
      );
      return;
    }

    // Check for breach reason before allowing closed transitions
    if (newState === 'closed' && hasSlaBreached && !hasBreachReason) {
      // Determine which target type is missing a breach reason
      let missingTargetType: 'RESPONSE' | 'ONGOING' | 'RESOLUTION' | undefined;
      if (isSlaTargetBreached('RESPONSE') && !change?.responseBreachReason) {
        missingTargetType = 'RESPONSE';
      } else if (isSlaTargetBreached('ONGOING') && !change?.ongoingBreachReason) {
        missingTargetType = 'ONGOING';
      } else if (isSlaTargetBreached('RESOLUTION') && !change?.resolutionBreachReason) {
        missingTargetType = 'RESOLUTION';
      }

      if (missingTargetType) {
        const targetLabel = missingTargetType.charAt(0) + missingTargetType.slice(1).toLowerCase();
        toast.error(`Please provide a breach reason for the ${targetLabel} SLA before closing this ticket.`);
        setBreachReasonModal({ isOpen: true, targetType: missingTargetType });
        return;
      }
    }

    // Special handling for re_initiate state - open re-initiate modal
    if (newState === 're_initiate') {
      setReInitiateModal({
        isOpen: true,
        plannedStart: formatDateTimeForInput(change?.plannedStart),
        plannedEnd: formatDateTimeForInput(change?.plannedEnd)
      });
      return;
    }

    // Special handling for assess state - directly initiate peer review workflow
    if (newState === 'assess') {
      // Auto-select the peer-review group (there should only be one)
      const peerReviewGroup = peerReviewGroupsData && peerReviewGroupsData.length > 0 ? peerReviewGroupsData[0] : null;

      if (!peerReviewGroup) {
        toast.error('No peer review group configured. Please contact administrator.');
        return;
      }

      // Store the old state before making the API call
      const oldState = previousStateRef.current || change?.changeState || change?.state || 'Unknown';

      try {
        // Call the peer-review/initiate endpoint directly (no modal)
        const response = await api.post(`/changes/${id}/peer-review/initiate`, {
          peerReviewGroupId: peerReviewGroup.id
        });

        if (response.data.success) {
          toast.success(response.data.message || 'Peer review initiated. Approval emails sent to group members.');
          // Refresh the change data to update state
          const refetchResult = await refetch();
          const refetchedChange = refetchResult?.data;
          await refetchPeerReviewStatus();
          queryClient.invalidateQueries({ queryKey: ['changes'] });
          
          // Log state change from 'new' to 'assess'
          const updatedChange = refetchedChange || change;
          const newStateValue = updatedChange?.changeState || updatedChange?.state || 'assess';
          
          logChangeAction(
            'Change Details - Edit',
            'Change State Updated',
            {
              oldState: oldState,
              newState: newStateValue,
            },
            updatedChange
          );
          
          // Update the previous state reference
          previousStateRef.current = newStateValue;
        } else {
          toast.error(response.data.message || 'Failed to initiate peer review');
        }
      } catch (error: any) {
        console.error('Failed to initiate peer review:', error);
        toast.error(error.response?.data?.message || 'Failed to initiate peer review');
      }
      return;
    }

    // Note: 'authorize' state transition is now automatic after CAB approval
    // No manual transition to authorize is allowed from the UI

    // Special handling for closed state - open close modal with close code selection
    if (newState === 'closed') {
      // Helper function to format datetime for input
      const formatDateForInput = (dateString: string | null | undefined) => {
        if (!dateString) return '';
        // Format as yyyy-MM-ddTHH:mm for datetime-local input (convert UTC to local)
        return formatDateTimeForInput(dateString);
      };

      setCloseForm({
        closeCode: '',
        closureNotes: '',
        actualStartDate: formatDateForInput(change?.actualStart),
        actualEndDate: formatDateForInput(change?.actualEnd)
      });
      setShowCloseModal(true);
      return;
    }

    setPendingStateChange({
      targetState: newState,
      actionLabel: `Change to ${getStateLabel(newState)}`
    });
  };

  const handleStatusModalSuccess = async (updatedChange: any) => {
    // Refetch the change data without showing loading state
    await refetch();
    // Invalidate the changes list to refresh the list view
    await queryClient.invalidateQueries({ queryKey: ['changes'] });
    
    // Log state change
    if (updatedChange && previousStateRef.current !== (updatedChange.changeState || updatedChange.state)) {
      logChangeAction(
        'Change Details - Edit',
        'Change State Updated',
        {
          oldState: previousStateRef.current || 'Unknown',
          newState: updatedChange.changeState || updatedChange.state || 'Unknown',
        },
        updatedChange
      );
      previousStateRef.current = updatedChange.changeState || updatedChange.state || null;
    }
    
    setPendingStateChange(null);
  };

  const handleStatusModalClose = () => {
    setPendingStateChange(null);
  };

  const handleCloseSubmit = () => {
    if (!closeForm.closeCode) {
      toast.error('Please select a close code');
      return;
    }

    if (!closeForm.actualStartDate) {
      toast.error('Please provide an actual start date');
      return;
    }

    if (!closeForm.actualEndDate) {
      toast.error('Please provide an actual end date');
      return;
    }

    // Validate date order
    const startDate = new Date(closeForm.actualStartDate);
    const endDate = new Date(closeForm.actualEndDate);
    if (endDate <= startDate) {
      toast.error('Actual end date must be after actual start date');
      return;
    }

    closeChangeMutation.mutate({
      changeCloseCode: closeForm.closeCode,
      closureNotes: closeForm.closureNotes,
      actualStartDate: datetimeLocalToUTC(closeForm.actualStartDate, preferences.timezone),
      actualEndDate: datetimeLocalToUTC(closeForm.actualEndDate, preferences.timezone)
    });
  };

  // Get available close codes based on current state
  const getAvailableCloseCodes = () => {
    const currentState = change?.changeState;

    if (currentState === 'scheduled') {
      return [
        { value: 'change_failed', label: 'Change Failed' },
        { value: 'change_failed_with_impact', label: 'Change Failed With Impact' }
      ];
    } else if (currentState === 'implement' || currentState === 'review') {
      return [
        { value: 'change_success', label: 'Change Success' },
        { value: 'change_success_with_impact', label: 'Change Success With Impact' },
        { value: 'change_failed', label: 'Change Failed' },
        { value: 'change_failed_with_impact', label: 'Change Failed With Impact' },
        { value: 'change_postponed', label: 'Change Postponed' }
      ];
    }

    return [];
  };

  // Note: handleAuthorizeSubmit and handleAuthorizeModalClose removed
  // State transition to 'authorize' now happens automatically after CAB approval

  const handleAssignClick = () => {
    setAssignForm({
      assignmentGroup: change?.assignmentGroup || '',
      assignedTo: change?.assignedTo || ''
    });
    setShowAssignModal(true);
  };

  const handleAssignSubmit = async () => {
    if (!assignForm.assignmentGroup || !assignForm.assignedTo) {
      alert('Please select both Assignment Group and Assigned To');
      return;
    }

    try {
      await api.put(`/changes/${id}`, {
        assignmentGroupId: assignForm.assignmentGroup,
        assignedToId: assignForm.assignedTo
      });

      // Log assignment action
      logChangeAction(
        'Change Details - Edit',
        'Change Assigned',
        {
          assignedGroupId: assignForm.assignmentGroup,
          assignedToId: assignForm.assignedTo,
        }
      );

      await queryClient.invalidateQueries({ queryKey: ['change', id] });
      setShowAssignModal(false);
      setAssignForm({ assignmentGroup: '', assignedTo: '' });
    } catch (error: any) {
      console.error('Failed to update assignment:', error);
      alert(error.response?.data?.message || 'Failed to update assignment');
    }
  };

  const handleAssignModalClose = () => {
    setShowAssignModal(false);
    setAssignForm({ assignmentGroup: '', assignedTo: '' });
  };

  const getCurrentStepIndex = () => {
    // Check if closed by resource cleanup (state=closed but changeState is not closed)
    const genericState = change.state?.toLowerCase();
    const changeState = change.changeState?.toLowerCase();
    if (genericState === 'closed' && changeState !== 'closed') {
      return workflowSteps.findIndex(step => step.id === 'closed');
    }
    const currentState = change.changeState || 'new';
    return workflowSteps.findIndex(step => step.id === currentState);
  };

  // Helper to check if an approval is for CAB stage (including legacy approvals with null stage)
  const isCabApproval = (approval: any): boolean => {
    // New multi-stage approvals have approvalStage === 'cab'
    // Legacy approvals have approvalStage === null/undefined (before multi-stage workflow)
    // Exclude peer_review and customer stage approvals
    return approval.approvalStage === 'cab' ||
           (approval.approvalStage !== 'peer_review' && approval.approvalStage !== 'customer' && !approval.approvalStage);
  };

  const getApprovalStatus = () => {
    if (!change.cabRequired || !change.approvals || change.approvals.length === 0) {
      return { approved: 0, rejected: 0, pending: 0, total: 0 };
    }

    // Count CAB stage approvals (including legacy approvals with null stage)
    const cabApprovals = change.approvals.filter(isCabApproval);
    const approved = cabApprovals.filter((a: Approval) => a.state === 'approved').length;
    const rejected = cabApprovals.filter((a: Approval) => a.state === 'rejected').length;
    const pending = cabApprovals.filter((a: Approval) => a.state === 'pending').length;

    // For total, use approval group members count
    const totalExpected = approvalGroupMembersData ? approvalGroupMembersData.length : cabApprovals.length;

    return { approved, rejected, pending, total: totalExpected };
  };

  const approvalStatus = getApprovalStatus();

  // Get the first CAB approver's name (for "first approval wins" display)
  const getFirstCabApproverName = (): string | null => {
    if (!change.approvals || change.approvals.length === 0) return null;
    const firstCabApproval = change.approvals.find(
      (a: Approval) => isCabApproval(a) && a.state === 'approved'
    );
    if (firstCabApproval?.User) {
      return `${firstCabApproval.User.firstName || ''} ${firstCabApproval.User.lastName || ''}`.trim() || null;
    }
    return null;
  };

  const firstCabApproverName = getFirstCabApproverName();

  const getNextStates = (state: string | undefined | null): string[] => {
    // Default to 'new' if state is null or undefined
    const currentState = state || 'new';

    switch (currentState) {
      case 'new':
        return ['assess', 'cancelled'];
      case 'assess':
        // Note: 'authorize' is not available here - state moves to authorize automatically after CAB approval
        return ['cancelled'];
      case 'authorize':
        return ['scheduled', 'cancelled'];
      case 'scheduled':
        return ['implement'];
      case 'implement':
        return ['review', 'closed'];
      case 'review':
        return ['closed'];
      case 'closed':
      case 'cancelled':
        return [];
      default:
        console.warn('getNextStates: unrecognized state:', currentState, '- defaulting to new');
        return ['assess', 'cancelled'];
    }
  };

  // Helper functions for approval/rejection logic
  const hasAnyRejection = () => {
    // If CAB is not required, rejections don't matter for workflow
    if (!change.cabRequired) return false;

    // Count CAB stage rejections (including legacy approvals with null stage)
    return change.approvals?.some((a: any) => a.state === 'rejected' && isCabApproval(a)) || false;
  };

  const hasAnyApproval = () => {
    // If CAB is not required, approvals don't matter for workflow
    if (!change.cabRequired) return false;

    // Count CAB stage approvals (including legacy approvals with null stage)
    return change.approvals?.some((a: any) => a.state === 'approved' && isCabApproval(a)) || false;
  };
  
  const canShowScheduleButton = () => {
    // Must be in authorize state
    if (change.changeState !== 'authorize') {
      return false;
    }

    // Customer approval is ALWAYS required
    if (!change.customerApprovalRequested || change.customerApprovalStatus !== 'approved') {
      return false;
    }

    // If CAB is not required, customer approval is sufficient
    if (!change.cabRequired) {
      return true;
    }

    // If CAB is required, need at least one approval and no rejections
    return hasAnyApproval() && !hasAnyRejection();
  };
  
  const userHasVoted = (userId: string) => {
    return change.approvals?.some((a: any) => a.approverId === userId) || false;
  };

  const isUserInApprovalGroup = (userId: string) => {
    return approvalGroupMembersData?.some((member: any) => member.id === userId) || false;
  };

  // Check if user has a pending customer approval
  const userHasPendingCustomerApproval = (userId: string) => {
    if (!change.customerApprovalRequested || change.customerApprovalStatus !== 'pending') {
      return false;
    }
    return change.approvals?.some((a: any) => a.approverId === userId && a.state === 'pending') || false;
  };

  const canUserApprove = (userId: string) => {
    // Super admin can always approve
    if ((user as any)?.role?.name === 'super_admin') return true;
    // Regular users must be in the approval group
    return isUserInApprovalGroup(userId);
  };

  const isUserAdminOrSuperAdmin = () => {
    // Check if user is super admin (GOD role type or level 4) or admin (level 3+)
    const userRole = (user as any)?.role;

    // Handle case where role is a string directly
    if (typeof userRole === 'string') {
      return userRole === 'super_admin' || userRole === 'admin';
    }

    // Handle case where role is an object
    return userRole?.roleType === 'GOD' ||
           userRole?.level >= 3 ||
           userRole?.name === 'super_admin' ||
           userRole?.name === 'admin';
  };
  
  const hasSuperAdminApproval = () => {
    if (!change.approvals || !approvalGroupMembersData) return false;
    const groupMemberIds = approvalGroupMembersData.map((member: any) => member.id);
    // Only check CAB stage approvals - exclude peer_review and customer approvals
    return change.approvals.some((a: Approval) =>
      a.state === 'approved' &&
      a.approvalStage === 'cab' &&
      !groupMemberIds.includes(a.approverId)
    );
  };

  // Helper function to check if current time is within planned implementation window
  const isWithinPlannedWindow = () => {
    // Support both plannedStart and plannedStartDate field names
    const startDate = change.plannedStart || change.plannedStartDate;
    const endDate = change.plannedEnd || change.plannedEndDate;

    if (!startDate || !endDate) {
      // If no planned dates, allow implementation anytime
      return true;
    }

    const now = new Date();
    const plannedStart = new Date(startDate);
    const plannedEnd = new Date(endDate);

    return now >= plannedStart && now <= plannedEnd;
  };

  const getVisibleStateTransitions = () => {
    const currentState = change.changeState || 'new';
    const nextStates = getNextStates(currentState);

    // Filter out 'scheduled' if conditions aren't met
    if (nextStates.includes('scheduled') && !canShowScheduleButton()) {
      return nextStates.filter(state => state !== 'scheduled');
    }

    // Note: Implementation window restriction has been removed - users can now transition to 'implement' at any time

    return nextStates;
  };

  const getStateLabel = (state: string | undefined | null): string => {
    const currentState = state || 'new';
    // Handle re_initiate specially
    if (currentState === 're_initiate') return 'Re-Initiate';
    const found = workflowSteps.find(s => s.id === currentState);
    return found ? found.name : currentState.charAt(0).toUpperCase() + currentState.slice(1);
  };

  /**
   * Check if the ticket was closed by the resource cleanup service
   * This happens when the generic `state` is "closed" but `changeState` is not "closed"
   * (i.e., the linked Azure resource was deleted)
   */
  const isClosedByResourceCleanup = (): boolean => {
    if (!change) return false;
    const genericState = change.state?.toLowerCase();
    const changeState = change.changeState?.toLowerCase();
    return genericState === 'closed' && changeState !== 'closed';
  };

  /**
   * Get the effective state to display, prioritizing resource cleanup closure
   */
  const getEffectiveDisplayState = (): string => {
    if (isClosedByResourceCleanup()) {
      return 'closed';
    }
    return change?.changeState || 'new';
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <button
          onClick={navigateBackToChanges}
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeftIcon className="mr-2 h-4 w-4" />
          Back to Changes
        </button>

        <div className="flex gap-2">
          {/* Assign To Me Button */}
          {shouldShowAssignToMeButton() && (
            <button
              onClick={handleAssignToMe}
              disabled={assignToMeMutation.isPending}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              {assignToMeMutation.isPending ? 'Assigning...' : 'Assign To Me'}
            </button>
          )}

          {/* Record Time Button - Show when assigned to current user and state is not new/closed/cancelled */}
          {change &&
           user &&
           (change.User_Case_assignedToIdToUser?.id === user.id || change.assignedToId === user.id) &&
           change.changeState !== 'new' &&
           change.changeState !== 'closed' &&
           change.changeState !== 'cancelled' && (
            <button
              onClick={() => setRecordTimeModalOpen(true)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <ClockIcon className="h-5 w-5 mr-2" />
              Record Time
            </button>
          )}

          {/* Actions Dropdown - Show if user can perform actions (not state-restricted) */}
          {canPerformActions && (
          <Menu as="div" className="relative inline-block text-left">
          <div>
            <Menu.Button className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
              <TicketIcon className="h-5 w-5 mr-2" />
              Actions
              <ChevronDownIcon className="ml-2 h-4 w-4" />
            </Menu.Button>
          </div>

          <Menu.Items className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
            <div className="py-1">
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={() => setDuplicateModalOpen(true)}
                    className={`${
                      active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                    } flex items-center w-full px-4 py-2 text-sm`}
                  >
                    <TicketIcon className="h-5 w-5 mr-3" />
                    Duplicate Change
                  </button>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={() => setCreateServiceRequestModalOpen(true)}
                    className={`${
                      active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                    } flex items-center w-full px-4 py-2 text-sm`}
                  >
                    <ChatBubbleLeftRightIcon className="h-5 w-5 mr-3" />
                    Create Service Request
                  </button>
                )}
              </Menu.Item>
            </div>
          </Menu.Items>
        </Menu>
          )}
        </div>
      </div>

      {/* Workflow Progress */}
      <div className="bg-white shadow rounded-lg mb-6 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Change Workflow</h3>
        <nav aria-label="Progress">
          <ol className="flex items-center">
            {workflowSteps.map((step, stepIdx) => {
              const currentIdx = getCurrentStepIndex();
              const Icon = step.icon;
              const isCancelled = change.changeState === 'cancelled';
              // Consider closed if changeState is closed OR if closed by resource cleanup
              const isClosedByCleanup = change.state?.toLowerCase() === 'closed' && change.changeState?.toLowerCase() !== 'closed';
              const isClosed = change.changeState === 'closed' || isClosedByCleanup;

              // When closed, we need to determine which was the last actual state before closing
              // We can check the activities or infer from close code
              let lastStateBeforeClose = currentIdx;
              if (isClosed) {
                // If closed, the current index is 6 (closed)
                // We need to find what state it was closed FROM
                // For resource cleanup closure, use the changeState as the last state
                if (isClosedByCleanup && change.changeState) {
                  const fromIdx = workflowSteps.findIndex(s => s.id === change.changeState);
                  if (fromIdx !== -1) {
                    lastStateBeforeClose = fromIdx;
                  }
                }
                // Check activities for the last state change before closing
                const stateChanges = change.CaseActivity?.filter((a: any) =>
                  (a.activityType === 'state_change' && a.metadata?.to === 'closed') ||
                  a.activityType === 'resource_cleanup_closure'
                ) || [];

                if (stateChanges.length > 0) {
                  const lastStateChange = stateChanges[stateChanges.length - 1];
                  const fromState = lastStateChange.metadata?.from;
                  const fromIdx = workflowSteps.findIndex(s => s.id === fromState);
                  if (fromIdx !== -1) {
                    lastStateBeforeClose = fromIdx;
                  }
                }
              }

              const isComplete = isClosed ? stepIdx <= lastStateBeforeClose : stepIdx < currentIdx;
              const isCurrent = isClosed ? false : stepIdx === currentIdx;
              const isCurrentClosed = isClosed && step.id === 'closed';

              // Detect skipped states: if closed, states between last actual state and closed are skipped
              const isSkipped = isClosed &&
                                stepIdx > lastStateBeforeClose &&
                                stepIdx < currentIdx &&
                                step.id !== 'closed';

              return (
                <li key={step.id} className={stepIdx !== workflowSteps.length - 1 ? 'flex-1' : ''}>
                  <div className="flex items-center">
                    <div
                      className={`relative flex h-10 w-10 items-center justify-center rounded-full ${
                        isComplete ? 'bg-indigo-600' :
                        isCurrent || isCurrentClosed ? 'bg-indigo-600' :
                        isSkipped ? 'bg-yellow-200' :
                        'bg-gray-300'
                      } ${isCancelled ? 'opacity-50' : ''}`}
                    >
                      <Icon className={`h-6 w-6 ${
                        isComplete || isCurrent || isCurrentClosed ? 'text-white' :
                        isSkipped ? 'text-yellow-800' :
                        'text-gray-600'
                      }`} />
                    </div>
                    {stepIdx !== workflowSteps.length - 1 && (
                      <div className={`ml-4 flex-1 h-0.5 ${
                        isComplete ? 'bg-indigo-600' :
                        isSkipped ? 'bg-yellow-200' :
                        'bg-gray-300'
                      }`} />
                    )}
                  </div>
                  <div className="mt-2">
                    <span className={`text-sm font-medium ${
                      isComplete || isCurrent || isCurrentClosed ? 'text-gray-900' :
                      isSkipped ? 'text-yellow-800' :
                      'text-gray-500'
                    }`}>
                      {step.name}
                      {isSkipped && <span className="block text-xs text-yellow-600 mt-0.5">Skipped</span>}
                    </span>
                  </div>
                </li>
              );
            })}
          </ol>
        </nav>
        
        {/* Missing Approval Group Warning - Show when CAB required but no Approval Group set */}
        {change.cabRequired && !change.approvalGroupId && change.changeState !== 'closed' && change.changeState !== 'cancelled' && (
          <div className="mt-6 rounded-lg p-4 bg-red-50 border border-red-200">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-red-800">
                  Approval Group Required
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  <p className="font-medium">This change requires CAB approval, but no Approval Group has been selected.</p>
                  <p className="mt-1">You cannot progress to the next state until an Approval Group is assigned. Please click the Edit button and select an Approval Group.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Implementation Skipped Banner - show when change was closed from Scheduled state */}
        {change.changeState === 'closed' &&
         (change.changeCloseCode === 'change_failed' || change.changeCloseCode === 'change_failed_with_impact') && (
          <div className="mt-6 rounded-lg p-4 border bg-yellow-50 border-yellow-200">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" aria-hidden="true" />
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-yellow-800">
                  Implementation Skipped
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>This change was closed directly from the Scheduled state. The Implementation and Review steps were skipped.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Customer Approval Banner - show when customer approval is requested */}
        {change.customerApprovalRequested && (
          <div className={`mt-6 rounded-lg p-4 border ${
            change.customerApprovalStatus === 'approved' || change.approvalStage === 'complete' ? 'bg-green-50 border-green-200' :
            change.customerApprovalStatus === 'rejected' ? 'bg-red-50 border-red-200' :
            'bg-yellow-50 border-yellow-200'
          }`}>
            <div className="flex items-start">
              <div className="flex-shrink-0">
                {change.customerApprovalStatus === 'approved' || change.approvalStage === 'complete' ? (
                  <CheckIcon className="h-5 w-5 text-green-400" />
                ) : change.customerApprovalStatus === 'rejected' ? (
                  <XMarkIcon className="h-5 w-5 text-red-400" />
                ) : (
                  <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
                )}
              </div>
              <div className="ml-3 flex-1">
                <h3 className={`text-sm font-medium ${
                  change.customerApprovalStatus === 'approved' || change.approvalStage === 'complete' ? 'text-green-800' :
                  change.customerApprovalStatus === 'rejected' ? 'text-red-800' :
                  'text-yellow-800'
                }`}>
                  Customer Approval {change.customerApprovalStatus === 'approved' || change.approvalStage === 'complete' ? 'Granted' :
                                   change.customerApprovalStatus === 'rejected' ? 'Rejected' :
                                   'Required'}
                </h3>
                <div className={`mt-2 text-sm ${
                  change.customerApprovalStatus === 'approved' || change.approvalStage === 'complete' ? 'text-green-700' :
                  change.customerApprovalStatus === 'rejected' ? 'text-red-700' :
                  'text-yellow-700'
                }`}>
                  {(change.customerApprovalStatus === 'pending' || (!change.customerApprovalStatus && change.approvalStage !== 'complete')) && !change.contactId && (
                    <p>
                      {isContact()
                        ? 'As no specific contact was assigned, the approval request was sent to all customer contacts associated with this engagement. Any customer contact can approve.'
                        : 'The approval request was sent to all customer contacts associated with this engagement.'}
                    </p>
                  )}
                  {(change.customerApprovalStatus === 'pending' || (!change.customerApprovalStatus && change.approvalStage !== 'complete')) && change.contactId && (
                    <p>
                      {isContact()
                        ? 'This change request requires customer approval. Please review the details and provide your decision.'
                        : `Waiting for customer approval from ${change.User_Case_contactIdToUser?.firstName} ${change.User_Case_contactIdToUser?.lastName} (${change.User_Case_contactIdToUser?.email}).`}
                    </p>
                  )}
                  {(change.customerApprovalStatus === 'approved' || change.approvalStage === 'complete') && (
                    <div>
                      <p>This change has been approved by the customer.</p>
                      {change.User_Case_customerApprovedByIdToUser && (
                        <p className="mt-1">
                          Approved by {change.User_Case_customerApprovedByIdToUser.firstName} {change.User_Case_customerApprovedByIdToUser.lastName}
                          {' '}({change.User_Case_customerApprovedByIdToUser.email})
                        </p>
                      )}
                    </div>
                  )}
                  {change.customerApprovalStatus === 'rejected' && (
                    <div>
                      <p>This change has been rejected by the customer.</p>
                      {change.User_Case_customerApprovedByIdToUser && (
                        <p className="mt-1">
                          Rejected by {change.User_Case_customerApprovedByIdToUser.firstName} {change.User_Case_customerApprovedByIdToUser.lastName}
                          {' '}({change.User_Case_customerApprovedByIdToUser.email})
                        </p>
                      )}
                      {change.customerApprovalComments && (
                        <p className="mt-2 font-medium">Reason: {change.customerApprovalComments}</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Show Approve/Reject buttons only when pending and user can approve */}
                {change.customerApprovalStatus === 'pending' &&
                 ((isContact() && ((user as any)?.id === change.contactId || (!change.contactId && (user as any)?.engagementId === change.Engagement?.id))) ||
                  userHasPendingCustomerApproval((user as any)?.id)) && (
                  <div className="mt-4 flex gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setCustomerApprovalDecision('approved');
                        setShowCustomerApprovalResponseModal(true);
                      }}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                    >
                      <CheckIcon className="h-5 w-5 mr-2" />
                      Approve
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setCustomerApprovalDecision('rejected');
                        setShowCustomerApprovalResponseModal(true);
                      }}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Reject
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Manager Approval Banner - Show when customer_approver group member or super_admin can approve on behalf of customer */}
        {/* Note: Backend validates membership in customer_approver group, so this just checks basic eligibility */}
        {change.customerApprovalRequested &&
         change.customerApprovalStatus === 'pending' &&
         !isContact() &&
         !userHasPendingCustomerApproval((user as any)?.id) &&
         (change.canApproveOnBehalf === true || isSuperAdmin()) && (
          <div className="mt-6 rounded-lg p-4 bg-blue-50 border border-blue-200">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-5 w-5 text-blue-400" aria-hidden="true" />
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-blue-800">
                  Manager Approval on Behalf of Customer
                </h3>
                <div className="mt-2 text-sm text-blue-700">
                  <p>As the manager of the assigned group, you can approve this change request on behalf of the customer. The customer will be notified of your decision.</p>
                </div>
                <div className="mt-4 flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setCustomerApprovalDecision('approved');
                      setShowCustomerApprovalResponseModal(true);
                    }}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    <CheckIcon className="h-5 w-5 mr-2" />
                    Approve on Behalf of Customer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Prominent approval action section when in authorize state - CAB Approval */}
        <PermissionGate resource="changes" action="update">
          {change.changeState === 'authorize' && change.cabRequired && !canShowScheduleButton() && (
            <div className="mt-6 rounded-lg p-4 bg-yellow-50 border border-yellow-200">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" aria-hidden="true" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">
                    {approvalStatus.approved > 0 ? 'CAB Approval Complete' : 'Change Approval Required'}
                  </h3>
                  <div className="mt-1 text-sm text-yellow-700">
                    {approvalStatus.approved > 0 ? (
                      <p>CAB approval has been completed by {firstCabApproverName || 'a CAB member'}. No further CAB action is needed.</p>
                    ) : (
                      <p>This change is waiting for approval from the Change Advisory Board (CAB).</p>
                    )}
                    {hasAnyRejection() && (
                      <p className="mt-1 font-medium text-red-700">
                        This change has been rejected and needs to be reassessed.
                      </p>
                    )}
                  </div>

                  {/* Show approval actions if user can approve, CAB is required, and no approval exists yet (first approval wins) */}
                  {canUserApprove((user as any)?.id) && !hasAnyRejection() && !userHasVoted((user as any)?.id) && approvalStatus.approved === 0 && (
                    <div className="mt-4">
                      <div className="flex space-x-3">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setCabApprovalNotes('');
                            setShowCabApprovalModal(true);
                          }}
                          disabled={approvalMutation.isPending || userHasVoted((user as any)?.id)}
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                        >
                          {approvalMutation.isPending ? 'Processing...' : userHasVoted((user as any)?.id) ? 'Already Voted' : 'Approve'}
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setRejectionReason('');
                            setShowRejectionModal(true);
                          }}
                          disabled={approvalMutation.isPending || userHasVoted((user as any)?.id)}
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                        >
                          {approvalMutation.isPending ? 'Processing...' : userHasVoted((user as any)?.id) ? 'Already Voted' : 'Reject'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Show current approval status if CAB is required */}
                  <div className="mt-4">
                    <div className="flex items-center space-x-4 text-sm">
                      <span className="text-green-600 font-medium">
                        {approvalStatus.approved} Approved
                      </span>
                      <span className="text-red-600 font-medium">
                        {approvalStatus.rejected} Rejected
                      </span>
                      <span className="text-yellow-600 font-medium">
                        {approvalStatus.pending} Pending
                      </span>
                    </div>
                  </div>

                  {/* Show scheduling message if ready to schedule */}
                  {canShowScheduleButton() && (
                    <div className="mt-3 text-sm text-green-700 font-medium">
                      ✓ This change is ready to be scheduled for implementation.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </PermissionGate>

        {/* Explicit next-state action buttons */}
        <PermissionGate resource="changes" action="update">
          {canChangeState && getVisibleStateTransitions().length > 0 && (
            <div className="mt-4 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <span className="text-sm font-medium text-gray-700">Current Status:</span>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  isClosedByResourceCleanup()
                    ? 'bg-amber-100 text-amber-800'
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {getStateLabel(getEffectiveDisplayState())}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500">Change to:</span>
                {getVisibleStateTransitions().map((stateId) => (
                  <button
                    key={stateId}
                    onClick={() => handleStateChange(stateId)}
                    disabled={updateStateMutation.isPending}
                    className={`inline-flex items-center px-3 py-1 rounded-md text-xs font-medium border transition-colors ${
                      stateId === 'closed' ? 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100' :
                      stateId === 'cancelled' ? 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100' :
                      'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100'
                    } disabled:opacity-50`}
                  >
                    {updateStateMutation.isPending ? 'Updating...' : getStateLabel(stateId)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </PermissionGate>

        {change.changeState === 'scheduled' && (
          <div className={`mt-4 rounded-md p-4 ${isWithinPlannedWindow() ? 'bg-green-50 border border-green-200' : 'bg-blue-50 border border-blue-200'}`}>
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <ClockIcon className={`h-5 w-5 ${isWithinPlannedWindow() ? 'text-green-400' : 'text-blue-400'}`} aria-hidden="true" />
              </div>
              <div className="ml-3">
                <h3 className={`text-sm font-medium ${isWithinPlannedWindow() ? 'text-green-800' : 'text-blue-800'}`}>
                  {isWithinPlannedWindow() ? '✓ Implementation Window Open' : 'Scheduled for Implementation'}
                </h3>
                <div className={`mt-2 text-sm ${isWithinPlannedWindow() ? 'text-green-700' : 'text-blue-700'}`}>
                  <p>
                    Implementation can be done between{' '}
                    <span className="font-semibold">
                      {(change.plannedStart || change.plannedStartDate)
                        ? formatDateTime(change.plannedStart || change.plannedStartDate, true)
                        : 'Not set'}
                    </span>
                    {' '}and{' '}
                    <span className="font-semibold">
                      {(change.plannedEnd || change.plannedEndDate)
                        ? formatDateTime(change.plannedEnd || change.plannedEndDate, true)
                        : 'Not set'}
                    </span>
                    {' '}as planned.
                  </p>
                  {!isWithinPlannedWindow() && (change.plannedStart || change.plannedStartDate) && new Date() < new Date(change.plannedStart || change.plannedStartDate) && (
                    <p className="mt-1 text-xs">
                      The implementation window will open in{' '}
                      {Math.ceil((new Date(change.plannedStart || change.plannedStartDate).getTime() - new Date().getTime()) / (1000 * 60 * 60))} hours.
                    </p>
                  )}
                  {!isWithinPlannedWindow() && (change.plannedEnd || change.plannedEndDate) && new Date() > new Date(change.plannedEnd || change.plannedEndDate) && (
                    <p className="mt-1 text-xs text-amber-700">
                      ⚠ The planned implementation window has passed.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        {change.changeState === 'cancelled' && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-3">
            <p className="text-sm text-red-800">This change has been cancelled</p>
          </div>
        )}
        {/* Resource Cleanup Closure Banner - takes priority over normal closed banner */}
        {isClosedByResourceCleanup() && (
          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-md p-3">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 mr-2" />
              <div>
                <p className="text-sm font-medium text-amber-800">Ticket is closed as resource got deleted</p>
                <p className="text-xs text-amber-700 mt-1">The linked Azure resource was deleted from the cloud provider. This ticket has been automatically closed and cannot be edited.</p>
              </div>
            </div>
          </div>
        )}
        {change.changeState === 'closed' && !isClosedByResourceCleanup() && (
          <div className="mt-4 bg-gray-50 border border-gray-200 rounded-md p-3">
            <p className="text-sm text-gray-800">This change has been closed and cannot be edited</p>
          </div>
        )}
      </div>

      {/* Change Header */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <h1 className="text-xl font-semibold text-gray-900">
                {change.number}{change.shortDescription && <span className="text-base font-normal text-gray-600"> - {stripHtml(change.shortDescription)}</span>}
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Created on {formatDateTime(change.createdAt)}
                {change.createdFromServiceNow && (
                  <span className="ml-2 text-indigo-700">
                    Linked from {getExternalSystemDisplayName(change.serviceNowLink?.externalSystem)}
                  </span>
                )}
                {change.syncedToServiceNow && (
                  <span className="ml-2 text-indigo-700">
                    Linked to {getExternalSystemDisplayName(change.serviceNowLink?.externalSystem)}
                  </span>
                )}
              </p>
            </div>
            <div className="flex items-center justify-end gap-3 flex-wrap flex-shrink-0">
              {change.changeType && (
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${changeTypeColors[change.changeType as keyof typeof changeTypeColors]}`}>
                  {change.changeType.charAt(0).toUpperCase() + change.changeType.slice(1)}
                </span>
              )}
              {change.risk && (
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${riskColors[change.risk as keyof typeof riskColors]}`}>
                  {change.risk.charAt(0).toUpperCase() + change.risk.slice(1)} Risk
                </span>
              )}
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${priorityColors[change.priority as keyof typeof priorityColors]}`}>
                Priority {priorityLabels[change.priority as keyof typeof priorityLabels]}
              </span>
              {!isEditing && (
                (canEdit && change.changeState && ['new', 'assess', 'authorize'].includes(change.changeState)) ||
                (canEditAssignment && change.changeState && !['closed', 'cancelled'].includes(change.changeState))
              ) && (
                <button
                  onClick={handleEdit}
                  className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  title={editAccessReason}
                >
                  <PencilIcon className="h-4 w-4 mr-1" />
                  Edit
                </button>
              )}
              {/* Assign Button - Show until implement state */}
              <PermissionGate resource="changes" action="update">
                {canEdit && change.changeState && ['new', 'assess', 'authorize', 'scheduled'].includes(change.changeState) && (
                  <button
                    onClick={handleAssignClick}
                    className="inline-flex items-center px-3 py-1 border border-transparent rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                  >
                    Assign
                  </button>
                )}
              </PermissionGate>
              <ServiceNowKnownField
                placement="header"
                pulseNumber={change.number}
                serviceNowNumber={change.serviceNowNumber}
                serviceNowKnown={change.serviceNowKnown}
                serviceNowLinked={change.serviceNowLinked}
                createdFromServiceNow={change.createdFromServiceNow}
                syncedToServiceNow={change.syncedToServiceNow}
                externalSystem={change.serviceNowLink?.externalSystem}
              />
            </div>
          </div>
        </div>

        {/* SLA Timers */}
        <SlaInstancesDisplay
          slaInstances={slaInstances}
          onBreachDetected={() => {
            // Trigger immediate refetch when UI detects breach
            queryClient.invalidateQueries({ queryKey: ['sla-instances', 'CHANGE', id] });
          }}
          breachReasons={breachReasons}
          onRequestBreachReason={(targetType) => setBreachReasonModal({ isOpen: true, targetType })}
        />

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex">
            <button
              onClick={() => setActiveTab('details')}
              className={`py-2 px-6 border-b-2 font-medium text-sm ${
                activeTab === 'details'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Details
            </button>
            <button
              onClick={() => setActiveTab('schedule')}
              className={`py-2 px-6 border-b-2 font-medium text-sm ${
                activeTab === 'schedule'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Schedule
            </button>
            <button
              onClick={() => setActiveTab('notes')}
              className={`py-2 px-6 border-b-2 font-medium text-sm ${
                activeTab === 'notes'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Notes
            </button>
            {isChangeStateAssessOrHigher(change.changeState) && (
              <button
                onClick={() => setActiveTab('approval')}
                className={`py-2 px-6 border-b-2 font-medium text-sm ${
                  activeTab === 'approval'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Approvals
              </button>
            )}
            <button
              onClick={() => setActiveTab('closure')}
              className={`py-2 px-6 border-b-2 font-medium text-sm flex items-center ${
                activeTab === 'closure'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Closure
              {(change.changeState === 'closed' || change.changeState === 'cancelled') && (
                <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                  {change.changeState === 'cancelled' ? '!' : '✓'}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('attachments')}
              className={`py-2 px-6 border-b-2 font-medium text-sm ${
                activeTab === 'attachments'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Attachments
            </button>
            <button
              onClick={() => setActiveTab('activities')}
              className={`py-2 px-6 border-b-2 font-medium text-sm ${
                activeTab === 'activities'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Activities
            </button>
            <button
              onClick={() => setActiveTab('related')}
              className={`py-2 px-6 border-b-2 font-medium text-sm ${
                activeTab === 'related'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Related Records
            </button>
          </nav>
        </div>

        <div className="p-6">
          {errors.submit && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-sm text-red-800">{errors.submit}</p>
            </div>
          )}

          {/* Details Tab */}
          {activeTab === 'details' && (
            isEditing ? (
              <div className="space-y-6">
                {/* Backend Error Banner */}
                {backendError && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-red-800">Error saving change</h3>
                        <p className="mt-1 text-sm text-red-700">{backendError}</p>
                      </div>
                    </div>
                  </div>
                )}

                {isAssignmentOnlyEditMode && (
                  <div className="rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                    You can update Assignment Group and Assigned To for this ticket. Other fields are read-only.
                  </div>
                )}

                <div className={isAssignmentOnlyEditMode ? ASSIGNMENT_ONLY_LOCK_CLASS : 'contents'}>
                {/* Edit Form */}
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Number
                        </label>
                      </div>
                      <div className="col-span-9">
                        <input
                          type="text"
                          value={change.number || ''}
                          readOnly
                          className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Change Type <span className="text-red-500">*</span>
                        </label>
                      </div>
                      <div className="col-span-9">
                        <input
                          type="text"
                          value={editForm.changeType ? editForm.changeType.charAt(0).toUpperCase() + editForm.changeType.slice(1) : 'Normal'}
                          readOnly
                          className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Risk Assessment <span className="text-red-500">*</span>
                        </label>
                      </div>
                      <div className="col-span-9">
                        <select
                          value={editForm.risk || 'low'}
                          onChange={(e) => {
                            const newRisk = e.target.value;
                            const newPriority = calculatePriorityFromImpactRisk(editForm.impact || 'low_3', newRisk);
                            setEditForm({ ...editForm, risk: newRisk, priority: newPriority });
                            if (errors.risk) {
                              setErrors(prev => ({ ...prev, risk: '' }));
                            }
                          }}
                          className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 ${
                            errors.risk
                              ? 'border-red-300 focus:ring-red-400'
                              : 'border-gray-300 focus:ring-blue-400'
                          }`}
                        >
                          <option value="low">3 - Low</option>
                          <option value="moderate">2 - Moderate</option>
                          <option value="high">1 - High</option>
                        </select>
                        {errors.risk && (
                          <p className="mt-1 text-sm text-red-600">{errors.risk}</p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Impact <span className="text-red-500">*</span>
                        </label>
                      </div>
                      <div className="col-span-9">
                        <select
                          value={editForm.impact || 'low_3'}
                          onChange={(e) => {
                            const newImpact = e.target.value;
                            const newPriority = calculatePriorityFromImpactRisk(newImpact, editForm.risk || 'low');
                            setEditForm({ ...editForm, impact: newImpact, priority: newPriority });
                            if (errors.impact) {
                              setErrors(prev => ({ ...prev, impact: '' }));
                            }
                          }}
                          className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 ${
                            errors.impact
                              ? 'border-red-300 focus:ring-red-400'
                              : 'border-gray-300 focus:ring-blue-400'
                          }`}
                        >
                          <option value="high_1">1 - High</option>
                          <option value="medium_2">2 - Medium</option>
                          <option value="low_3">3 - Low</option>
                        </select>
                        {errors.impact && (
                          <p className="mt-1 text-sm text-red-600">{errors.impact}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Priority
                        </label>
                      </div>
                      <div className="col-span-9">
                        <input
                          type="text"
                          value={(() => {
                            // Calculate priority in real-time based on current form values
                            const currentPriority = calculatePriorityFromImpactRisk(
                              editForm.impact || 'low_3',
                              editForm.risk || 'low'
                            );
                            return `${currentPriority} - ${priorityLabels[currentPriority] || 'Unknown'}`;
                          })()}
                          readOnly
                          className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Customer and Engagement Fields */}
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Customer <span className="text-red-500">*</span>
                        </label>
                      </div>
                      <div className="col-span-9">
                        <input
                          type="text"
                          value={change.Customer?.accountName || 'Not assigned'}
                          readOnly
                          className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Engagement <span className="text-red-500">*</span>
                        </label>
                      </div>
                      <div className="col-span-9">
                        <input
                          type="text"
                          value={change.Engagement?.name || 'Not assigned'}
                          readOnly
                          className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contact Field */}
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Contact
                        </label>
                      </div>
                      <div className="col-span-9">
                        <Combobox
                          value={editForm.contactId || ''}
                          onChange={(value: string) => {
                            setEditForm({ ...editForm, contactId: value || '' });
                            if (errors.contactId) {
                              setErrors(prev => ({ ...prev, contactId: '' }));
                            }
                          }}
                          disabled={!editForm.customerId}
                        >
                          <div className="relative">
                            <Combobox.Input
                              className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 ${!editForm.customerId ? 'bg-gray-50 text-gray-400' : ''} ${errors.contactId ? 'border-red-300' : 'border-gray-300 focus:ring-blue-400'}`}
                              displayValue={(contactId: string) => {
                                const contact = (contactsData?.contacts || []).find((c: any) => c.id === contactId);
                                return contact ? contact.name : '';
                              }}
                              onChange={(e) => setContactQuery(e.target.value)}
                              placeholder={!editForm.customerId ? 'Select Customer first' : 'Select Contact'}
                            />
                            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                              <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                            </Combobox.Button>
                            <Transition
                              as={Fragment}
                              leave="transition ease-in duration-100"
                              leaveFrom="opacity-100"
                              leaveTo="opacity-0"
                              afterLeave={() => setContactQuery('')}
                            >
                              <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                {(contactsData?.contacts || [])
                                  .filter((contact: any) => `${contact.name} ${contact.email}`.toLowerCase().includes(contactQuery.toLowerCase()))
                                  .map((contact: any) => (
                                  <Combobox.Option
                                    key={contact.id}
                                    value={contact.id}
                                    className={({ active }) =>
                                      `relative cursor-pointer select-none py-2 pl-3 pr-9 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`
                                    }
                                  >
                                    {({ selected, active }) => (
                                      <>
                                        <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                          {contact.name}
                                        </span>
                                        {selected && (
                                          <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                            <CheckIcon className="h-4 w-4" />
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </Combobox.Option>
                                ))}
                              </Combobox.Options>
                            </Transition>
                          </div>
                        </Combobox>
                        {errors.contactId && (
                          <p className="mt-1 text-xs text-red-500">{errors.contactId}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Resource and Resource ID / Custom Resources - editable until Scheduled */}
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Resource {canEditResourceAndCustom && <span className="text-red-500">*</span>}
                        </label>
                      </div>
                      <div className="col-span-9">
                        {canEditResourceAndCustom ? (
                          <Combobox
                            value={isOtherResource ? '__OTHER__' : (editForm.resourceId || '')}
                            onChange={(value: string) => {
                              if (value === '__OTHER__') {
                                setIsOtherResource(true);
                                setEditForm(prev => ({ ...prev, resourceId: '', otherResourceNames: prev.otherResourceNames || '' }));
                                setSelectedResourceIdValue('');
                              } else {
                                setIsOtherResource(false);
                                const selectedResource = (resourcesData || []).find((r: any) => r.id === value);
                                setSelectedResourceIdValue(selectedResource?.resourceId || '');
                                setEditForm(prev => ({ ...prev, resourceId: value || '', otherResourceNames: '' }));
                              }
                              if (errors.resourceId) setErrors(prev => ({ ...prev, resourceId: '' }));
                              if (errors.otherResourceNames) setErrors(prev => ({ ...prev, otherResourceNames: '' }));
                            }}
                            disabled={!editForm.engagementId}
                          >
                            <div className="relative">
                              <Combobox.Input
                                className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${!editForm.engagementId ? 'bg-gray-50 text-gray-400' : ''} ${errors.resourceId ? 'border-red-300' : 'border-gray-300'}`}
                                displayValue={(resourceId: string) => {
                                  if (resourceId === '__OTHER__' || isOtherResource) return 'Others (specify custom resources)';
                                  const resource = (resourcesData || []).find((r: any) => r.id === resourceId);
                                  return resource ? `${resource.name} (${resource.resourceType}) - ${resource.resourceGroupName}` : '';
                                }}
                                onChange={(e) => setResourceQuery(e.target.value)}
                                placeholder="Select Resource"
                              />
                              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                              </Combobox.Button>
                              <Transition
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                                afterLeave={() => setResourceQuery('')}
                              >
                                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                  {(resourcesData || [])
                                    .filter((resource: any) => `${resource.name} ${resource.resourceType} ${resource.resourceGroupName}`.toLowerCase().includes(resourceQuery.toLowerCase()))
                                    .map((resource: any) => (
                                      <Combobox.Option
                                        key={resource.id}
                                        value={resource.id}
                                        className={({ active }) =>
                                          `relative cursor-pointer select-none py-2 pl-3 pr-9 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`
                                        }
                                      >
                                        {({ selected, active }) => (
                                          <>
                                            <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                              {resource.name} ({resource.resourceType}) - {resource.resourceGroupName}
                                            </span>
                                            {selected && (
                                              <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                                <CheckIcon className="h-4 w-4" />
                                              </span>
                                            )}
                                          </>
                                        )}
                                      </Combobox.Option>
                                    ))}
                                  <Combobox.Option
                                    key="__OTHER__"
                                    value="__OTHER__"
                                    className={({ active }) =>
                                      `relative cursor-pointer select-none py-2 pl-3 pr-9 border-t border-gray-200 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`
                                    }
                                  >
                                    {({ selected, active }) => (
                                      <>
                                        <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                          Others (specify custom resources)
                                        </span>
                                        {selected && (
                                          <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                            <CheckIcon className="h-4 w-4" />
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </Combobox.Option>
                                </Combobox.Options>
                              </Transition>
                            </div>
                          </Combobox>
                        ) : (
                          <input
                            type="text"
                            value={isOtherResource ? 'Others (Custom)' : (change?.Resource?.name || '-')}
                            readOnly
                            className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                          />
                        )}
                        {canEditResourceAndCustom && errors.resourceId && (
                          <p className="mt-1 text-sm text-red-600">{errors.resourceId}</p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col-span-6">
                    <div className={`grid grid-cols-12 gap-4 ${isOtherResource && canEditResourceAndCustom ? 'items-start' : 'items-end'}`}>
                      <div className="col-span-3">
                        <label className={`block text-sm font-medium text-gray-700 ${isOtherResource && canEditResourceAndCustom ? 'mt-1' : 'mb-1'}`}>
                          {isOtherResource ? 'Resource Names' : 'Resource ID'}
                          {isOtherResource && canEditResourceAndCustom && <span className="text-red-500"> *</span>}
                        </label>
                      </div>
                      <div className="col-span-9">
                        {canEditResourceAndCustom && isOtherResource ? (
                          <>
                            <input
                              type="text"
                              value={editForm.otherResourceNames || ''}
                              onChange={(e) => {
                                setEditForm(prev => ({ ...prev, otherResourceNames: e.target.value }));
                                if (errors.otherResourceNames) setErrors(prev => ({ ...prev, otherResourceNames: '' }));
                              }}
                              placeholder="Enter resource names, separated by commas"
                              className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${errors.otherResourceNames ? 'border-red-300' : 'border-gray-300'}`}
                            />
                            {errors.otherResourceNames && (
                              <p className="mt-1 text-sm text-red-600">{errors.otherResourceNames}</p>
                            )}
                            <p className="mt-1 text-xs text-gray-500">Enter multiple resource names separated by commas</p>
                          </>
                        ) : (
                          <input
                            type="text"
                            value={isOtherResource
                              ? (editForm.otherResourceNames || '-')
                              : (selectedResourceIdValue || '-')}
                            readOnly
                            className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Planned Start and Planned End Fields */}
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Planned Start <span className="text-red-500">*</span>
                        </label>
                      </div>
                      <div className="col-span-9">
                        <DateTimePickerInput
                          value={editForm.plannedStart || ''}
                          onChange={(value) => {
                            setEditForm({ ...editForm, plannedStart: value });
                            if (errors.plannedStart) {
                              setErrors(prev => ({ ...prev, plannedStart: '' }));
                            }
                          }}
                          error={!!errors.plannedStart}
                          required
                          disabled={!canEditPlannedDates}
                        />
                        {errors.plannedStart && (
                          <p className="mt-1 text-sm text-red-600">{errors.plannedStart}</p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Planned End <span className="text-red-500">*</span>
                        </label>
                      </div>
                      <div className="col-span-9">
                        <DateTimePickerInput
                          value={editForm.plannedEnd || ''}
                          onChange={(value) => {
                            setEditForm({ ...editForm, plannedEnd: value });
                            if (errors.plannedEnd) {
                              setErrors(prev => ({ ...prev, plannedEnd: '' }));
                            }
                          }}
                          min={editForm.plannedStart || undefined}
                          error={!!errors.plannedEnd}
                          required
                          disabled={!canEditPlannedDates}
                        />
                        {errors.plannedEnd && (
                          <p className="mt-1 text-sm text-red-600">{errors.plannedEnd}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                </div>

                {/* Assignment Group and Assigned To Fields */}
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Assignment Group <span className="text-red-500">*</span>
                        </label>
                      </div>
                      <div className="col-span-9">
                        <Combobox
                          value={editForm.assignmentGroup || ''}
                          onChange={(value: string) => {
                            setEditForm({
                              ...editForm,
                              assignmentGroup: value || '',
                              assignedTo: '' // Clear assigned to when assignment group changes
                            });
                          }}
                        >
                          <div className="relative">
                            <Combobox.Input
                              className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400"
                              displayValue={(groupId: string) => {
                                const group = (assignmentGroupsData || []).find((g: any) => g.id === groupId);
                                return group ? group.name : '';
                              }}
                              onChange={(e) => setAssignmentGroupQuery(e.target.value)}
                              placeholder="Select Assignment Group"
                            />
                            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                              <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                            </Combobox.Button>
                            <Transition
                              as={Fragment}
                              leave="transition ease-in duration-100"
                              leaveFrom="opacity-100"
                              leaveTo="opacity-0"
                              afterLeave={() => setAssignmentGroupQuery('')}
                            >
                              <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                {(assignmentGroupsData || [])
                                  .filter((group: any) => group && group.id && group.name.toLowerCase().includes(assignmentGroupQuery.toLowerCase()))
                                  .map((group: any) => (
                                  <Combobox.Option
                                    key={group.id}
                                    value={group.id}
                                    className={({ active }) =>
                                      `relative cursor-pointer select-none py-2 pl-3 pr-9 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`
                                    }
                                  >
                                    {({ selected, active }) => (
                                      <>
                                        <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                          {group.name}
                                        </span>
                                        {selected && (
                                          <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                            <CheckIcon className="h-4 w-4" />
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </Combobox.Option>
                                ))}
                              </Combobox.Options>
                            </Transition>
                          </div>
                        </Combobox>
                      </div>
                    </div>
                  </div>
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Assigned To
                        </label>
                      </div>
                      <div className="col-span-9">
                        <Combobox
                          value={editForm.assignedTo || ''}
                          onChange={(value: string) => {
                            setEditForm({ ...editForm, assignedTo: value || null });
                            if (errors.assignedTo) {
                              setErrors(prev => ({ ...prev, assignedTo: '' }));
                            }
                          }}
                          disabled={!editForm.assignmentGroup}
                        >
                          <div className="relative">
                            <Combobox.Input
                              className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 ${!editForm.assignmentGroup ? 'bg-gray-50 text-gray-400' : ''} ${errors.assignedTo ? 'border-red-300' : 'border-gray-300 focus:ring-blue-400'}`}
                              displayValue={(memberId: string) => {
                                const member = (groupMembersData || []).find((m: any) => m.id === memberId);
                                return member ? `${member.firstName} ${member.lastName}` : '';
                              }}
                              onChange={(e) => setAssignedToQuery(e.target.value)}
                              placeholder="Select Assignee"
                            />
                            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                              <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                            </Combobox.Button>
                            <Transition
                              as={Fragment}
                              leave="transition ease-in duration-100"
                              leaveFrom="opacity-100"
                              leaveTo="opacity-0"
                              afterLeave={() => setAssignedToQuery('')}
                            >
                              <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                {(groupMembersData || [])
                                  .filter((member: any) => member && member.id && `${member.firstName} ${member.lastName}`.toLowerCase().includes(assignedToQuery.toLowerCase()))
                                  .map((member: any) => (
                                  <Combobox.Option
                                    key={member.id}
                                    value={member.id}
                                    className={({ active }) =>
                                      `relative cursor-pointer select-none py-2 pl-3 pr-9 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`
                                    }
                                  >
                                    {({ selected, active }) => (
                                      <>
                                        <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                          {member.firstName} {member.lastName}
                                        </span>
                                        {selected && (
                                          <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                            <CheckIcon className="h-4 w-4" />
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </Combobox.Option>
                                ))}
                              </Combobox.Options>
                            </Transition>
                          </div>
                        </Combobox>
                        {errors.assignedTo && (
                          <p className="mt-1 text-sm text-red-600">{errors.assignedTo}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className={isAssignmentOnlyEditMode ? ASSIGNMENT_ONLY_LOCK_CLASS : 'contents'}>
                {/* CAB Required and Approval Group */}
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-start">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          CAB Required
                        </label>
                      </div>
                      <div className="col-span-9">
                        <div className="flex items-start">
                          <div className="flex items-center h-5">
                            <input
                              id="cabRequired"
                              name="cabRequired"
                              type="checkbox"
                              checked={editForm.cabRequired || false}
                              onChange={(e) => setEditForm({ ...editForm, cabRequired: e.target.checked })}
                              disabled={true}
                              className="focus:ring-blue-400 h-4 w-4 text-blue-600 border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                            />
                          </div>
                          <div className="ml-3 text-sm">
                            <label htmlFor="cabRequired" className="font-medium text-gray-500">
                              CAB approval needed
                              {editForm.changeType === 'standard' && <span className="text-gray-500 italic"> (Not required for Standard)</span>}
                              {(editForm.changeType === 'normal' || editForm.changeType === 'emergency') && <span className="text-gray-500 italic"> (Required for {editForm.changeType === 'normal' ? 'Normal' : 'Emergency'})</span>}
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  {editForm.cabRequired && (
                    <div className="col-span-6">
                      <div className="grid grid-cols-12 gap-4 items-end">
                        <div className="col-span-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Approval Group <span className="text-red-500">*</span>
                          </label>
                        </div>
                        <div className="col-span-9">
                          <Combobox
                            value={editForm.approvalGroup || ''}
                            onChange={(value: string) => {
                              setEditForm({ ...editForm, approvalGroup: value || '' });
                              if (errors.approvalGroup) {
                                setErrors(prev => ({ ...prev, approvalGroup: '' }));
                              }
                            }}
                          >
                            <div className="relative">
                              <Combobox.Input
                                className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${errors.approvalGroup ? 'border-red-300' : 'border-gray-300'}`}
                                displayValue={(groupId: string) => {
                                  const group = (approvalGroupsData || []).find((g: any) => g.id === groupId);
                                  return group ? group.name : '';
                                }}
                                onChange={(e) => setApprovalGroupQuery(e.target.value)}
                                placeholder="Select Approval Group"
                              />
                              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                              </Combobox.Button>
                              <Transition
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                                afterLeave={() => setApprovalGroupQuery('')}
                              >
                                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                  {(approvalGroupsData || [])
                                    .filter((group: any) => group && group.id && group.name.toLowerCase().includes(approvalGroupQuery.toLowerCase()))
                                    .map((group: any) => (
                                    <Combobox.Option
                                      key={group.id}
                                      value={group.id}
                                      className={({ active }) =>
                                        `relative cursor-pointer select-none py-2 pl-3 pr-9 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`
                                      }
                                    >
                                      {({ selected, active }) => (
                                        <>
                                          <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                            {group.name}
                                          </span>
                                          {selected && (
                                            <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                              <CheckIcon className="h-4 w-4" />
                                            </span>
                                          )}
                                        </>
                                      )}
                                    </Combobox.Option>
                                  ))}
                                </Combobox.Options>
                              </Transition>
                            </div>
                          </Combobox>
                          {errors.approvalGroup && (
                            <p className="mt-1 text-sm text-red-600">{errors.approvalGroup}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Short Description */}
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-12">
                    <div className="grid grid-cols-12 gap-4 items-start">
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Short Description <span className="text-red-500">*</span>
                        </label>
                      </div>
                      <div className="col-span-10">
                        <RichTextEditor
                          value={editForm.shortDescription || ''}
                          onChange={(value) => {
                            setEditForm({ ...editForm, shortDescription: value });
                            if (errors.shortDescription) {
                              setErrors(prev => ({ ...prev, shortDescription: '' }));
                            }
                          }}
                          minHeight="120px"
                          className={errors.shortDescription ? 'border-red-300' : ''}
                        />
                        {errors.shortDescription && (
                          <p className="mt-1 text-sm text-red-600">{errors.shortDescription}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-12">
                    <div className="grid grid-cols-12 gap-4 items-start">
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Description
                        </label>
                      </div>
                      <div className="col-span-10">
                        <RichTextEditor
                          value={editForm.description || ''}
                          onChange={(value) => {
                            setEditForm({ ...editForm, description: value });
                          }}
                          placeholder="Detailed description of the change"
                          minHeight="120px"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Reference Alert */}
                {change.ReferenceAlert && (
                  <div className="grid grid-cols-12 gap-6">
                    <div className="col-span-6">
                      <div className="grid grid-cols-12 gap-4 items-end">
                        <div className="col-span-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Reference Alert
                          </label>
                        </div>
                        <div className="col-span-9">
                          <Link
                            to={`/alerts/${change.ReferenceAlert.id}`}
                            className="inline-flex items-center px-3 py-1 border border-blue-300 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 text-sm h-8"
                          >
                            <TicketIcon className="h-4 w-4 mr-1" />
                            {change.ReferenceAlert.number}
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Justification */}
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-12">
                    <div className="grid grid-cols-12 gap-4 items-start">
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Justification <span className="text-red-500">*</span>
                        </label>
                      </div>
                      <div className="col-span-10">
                        <RichTextEditor
                          value={editForm.justification || ''}
                          onChange={(value) => {
                            setEditForm({ ...editForm, justification: value });
                            if (errors.justification) {
                              setErrors(prev => ({ ...prev, justification: '' }));
                            }
                          }}
                          placeholder="Business justification for this change"
                          minHeight="120px"
                          className={errors.justification ? 'border-red-300' : ''}
                        />
                        {errors.justification && (
                          <p className="mt-1 text-sm text-red-600">{errors.justification}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Risk & Impact Analysis */}
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-12">
                    <div className="grid grid-cols-12 gap-4 items-start">
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Risk & Impact Analysis <span className="text-red-500">*</span>
                        </label>
                      </div>
                      <div className="col-span-10">
                        <RichTextEditor
                          value={editForm.riskImpactAnalysis || ''}
                          onChange={(value) => {
                            setEditForm({ ...editForm, riskImpactAnalysis: value });
                            if (errors.riskImpactAnalysis) {
                              setErrors(prev => ({ ...prev, riskImpactAnalysis: '' }));
                            }
                          }}
                          placeholder="Assessment of risks and potential impact"
                          minHeight="120px"
                          className={errors.riskImpactAnalysis ? 'border-red-300' : ''}
                        />
                        {errors.riskImpactAnalysis && (
                          <p className="mt-1 text-sm text-red-600">{errors.riskImpactAnalysis}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Implementation Plan */}
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-12">
                    <div className="grid grid-cols-12 gap-4 items-start">
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Implementation Plan <span className="text-red-500">*</span>
                        </label>
                      </div>
                      <div className="col-span-10">
                        <RichTextEditor
                          value={editForm.implementationPlan || ''}
                          onChange={(value) => {
                            setEditForm({ ...editForm, implementationPlan: value });
                            if (errors.implementationPlan) {
                              setErrors(prev => ({ ...prev, implementationPlan: '' }));
                            }
                          }}
                          placeholder="Step-by-step plan for implementing the change"
                          minHeight="120px"
                          className={errors.implementationPlan ? 'border-red-300' : ''}
                        />
                        {errors.implementationPlan && (
                          <p className="mt-1 text-sm text-red-600">{errors.implementationPlan}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Backout Plan */}
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-12">
                    <div className="grid grid-cols-12 gap-4 items-start">
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Backout Plan <span className="text-red-500">*</span>
                        </label>
                      </div>
                      <div className="col-span-10">
                        <RichTextEditor
                          value={editForm.backoutPlan || ''}
                          onChange={(value) => {
                            setEditForm({ ...editForm, backoutPlan: value });
                            if (errors.backoutPlan) {
                              setErrors(prev => ({ ...prev, backoutPlan: '' }));
                            }
                          }}
                          placeholder="Steps to revert the change if issues occur"
                          minHeight="120px"
                          className={errors.backoutPlan ? 'border-red-300' : ''}
                        />
                        {errors.backoutPlan && (
                          <p className="mt-1 text-sm text-red-600">{errors.backoutPlan}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Test Plan */}
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-12">
                    <div className="grid grid-cols-12 gap-4 items-start">
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Test Plan <span className="text-red-500">*</span>
                        </label>
                      </div>
                      <div className="col-span-10">
                        <RichTextEditor
                          value={editForm.testPlan || ''}
                          onChange={(value) => {
                            setEditForm({ ...editForm, testPlan: value });
                            if (errors.testPlan) {
                              setErrors(prev => ({ ...prev, testPlan: '' }));
                            }
                          }}
                          placeholder="How the change will be tested and validated"
                          minHeight="120px"
                          className={errors.testPlan ? 'border-red-300' : ''}
                        />
                        {errors.testPlan && (
                          <p className="mt-1 text-sm text-red-600">{errors.testPlan}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                </div>

                <div className="flex justify-end space-x-2 pt-4 border-t">
                  <button
                    onClick={() => setIsEditing(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={updateChangeMutation.isPending}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {updateChangeMutation.isPending ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
              {/* Row 1: Number and Change Type */}
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Number
                      </label>
                    </div>
                    <div className="col-span-9">
                      <input
                        type="text"
                        value={change.number || ''}
                        readOnly
                        className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                      />
                    </div>
                  </div>
                </div>
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Change Type
                      </label>
                    </div>
                    <div className="col-span-9">
                      <input
                        type="text"
                        value={change.changeType ? change.changeType.charAt(0).toUpperCase() + change.changeType.slice(1) : 'Not set'}
                        readOnly
                        className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 2: Risk Assessment and Impact */}
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Risk Assessment
                      </label>
                    </div>
                    <div className="col-span-9">
                      <input
                        type="text"
                        value={change.risk ? (() => {
                          const riskLabels = {
                            'high': '1 - High',
                            'moderate': '2 - Moderate',
                            'low': '3 - Low'
                          };
                          return riskLabels[change.risk as keyof typeof riskLabels] || change.risk.charAt(0).toUpperCase() + change.risk.slice(1);
                        })() : 'Not set'}
                        readOnly
                        className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                      />
                    </div>
                  </div>
                </div>
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Impact
                      </label>
                    </div>
                    <div className="col-span-9">
                      <input
                        type="text"
                        value={getDisplayLabel(change.impact, impactLabels)}
                        readOnly
                        className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 3: Priority */}
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Priority
                      </label>
                    </div>
                    <div className="col-span-9">
                      <input
                        type="text"
                        value={(() => {
                          return change.priority ? `${change.priority} - ${priorityLabels[change.priority] || 'Unknown'}` : 'Not set';
                        })()}
                        readOnly
                        className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 4: Customer and Engagement */}
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Customer
                      </label>
                    </div>
                    <div className="col-span-9">
                      <input
                        type="text"
                        value={change.Customer?.accountName || 'Not assigned'}
                        readOnly
                        className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                      />
                    </div>
                  </div>
                </div>
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Engagement
                      </label>
                    </div>
                    <div className="col-span-9">
                      <input
                        type="text"
                        value={change.Engagement ? `${change.Engagement.name} - ${change.Engagement.status}` : 'Not assigned'}
                        readOnly
                        className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 5: Contact */}
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Contact
                      </label>
                    </div>
                    <div className="col-span-9">
                      <input
                        type="text"
                        value={change.User_Case_contactIdToUser ? `${change.User_Case_contactIdToUser.firstName} ${change.User_Case_contactIdToUser.lastName}` : 'Not assigned'}
                        readOnly
                        className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 6: Resource and Resource ID / Custom Resources */}
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Resource
                      </label>
                    </div>
                    <div className="col-span-9">
                      <input
                        type="text"
                        value={change.otherResourceNames ? 'Others (Custom)' : (change.Resource?.name || 'Not assigned')}
                        readOnly
                        className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                      />
                    </div>
                  </div>
                </div>
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {change.otherResourceNames ? 'Custom Resources' : 'Resource ID'}
                      </label>
                    </div>
                    <div className="col-span-9">
                      <input
                        type="text"
                        value={change.otherResourceNames || change.Resource?.resourceId || (change as any).resourceIdValue || 'Not assigned'}
                        readOnly
                        className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 7: Planned Start and Planned End */}
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Planned Start
                      </label>
                    </div>
                    <div className="col-span-9">
                      <input
                        type="text"
                        value={change.plannedStart ? formatDateTime(change.plannedStart) : 'Not set'}
                        readOnly
                        className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                      />
                    </div>
                  </div>
                </div>
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Planned End
                      </label>
                    </div>
                    <div className="col-span-9">
                      <input
                        type="text"
                        value={change.plannedEnd ? formatDateTime(change.plannedEnd) : 'Not set'}
                        readOnly
                        className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 8: Assignment Group and Assigned To */}
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Assignment Group <span className="text-red-500">*</span>
                      </label>
                    </div>
                    <div className="col-span-9">
                      <input
                        type="text"
                        value={(() => {
                          // First try the nested object structure
                          if (change.Group_Case_assignmentGroupIdToGroup?.name) {
                            return change.Group_Case_assignmentGroupIdToGroup.name;
                          }
                          // Then try assignmentGroup field with lookup
                          if (change.assignmentGroup && assignmentGroupsData) {
                            const group = assignmentGroupsData.find((g: any) => g.id === change.assignmentGroup);
                            return group?.name || change.assignmentGroup;
                          }
                          return 'Not assigned';
                        })()}
                        readOnly
                        className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                      />
                    </div>
                  </div>
                </div>
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Assigned To
                      </label>
                    </div>
                    <div className="col-span-9 flex gap-2">
                      <input
                        type="text"
                        value={(() => {
                          // First try the nested object structure
                          if (change.User_Case_assignedToIdToUser) {
                            const user = change.User_Case_assignedToIdToUser;
                            return `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'Unknown User';
                          }
                          // Then try assignedTo field with lookup
                          if (change.assignedTo && usersData?.users) {
                            const user = usersData.users.find((u: any) => u.id === change.assignedTo);
                            return user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email : change.assignedTo;
                          }
                          return 'Not assigned';
                        })()}
                        readOnly
                        className="flex-1 px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                      />
                      {shouldShowAssignToMeButton() && (
                        <button
                          onClick={handleAssignToMe}
                          disabled={assignToMeMutation.isPending}
                          className="px-3 py-1 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded disabled:opacity-50 whitespace-nowrap h-8"
                        >
                          {assignToMeMutation.isPending ? 'Assigning...' : 'Assign To Me'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 9: CAB Required and Approval Group */}
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-start">
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        CAB Required
                      </label>
                    </div>
                    <div className="col-span-9">
                      <div className="flex items-start">
                        <div className="flex items-center h-5">
                          <input
                            type="checkbox"
                            checked={change.cabRequired || false}
                            disabled
                            className="focus:ring-blue-400 h-4 w-4 text-blue-600 border-gray-300 rounded bg-gray-100"
                          />
                        </div>
                        <div className="ml-3 text-sm">
                          <span className="font-medium text-gray-500">
                            Change Advisory Board approval needed
                            {change.changeType === 'standard' && <span className="text-gray-500 italic"> (Not required for Standard changes)</span>}
                            {(change.changeType === 'normal' || change.changeType === 'emergency') && <span className="text-gray-500 italic"> (Required for {change.changeType === 'normal' ? 'Normal' : 'Emergency'} changes)</span>}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {change.cabRequired && (
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Approval Group
                        </label>
                      </div>
                      <div className="col-span-9">
                        <input
                          type="text"
                          value={(() => {
                            // First try the nested object structure
                            if (change.approvalGroup?.name) {
                              return change.approvalGroup.name;
                            }
                            // Then try approvalGroupId field with lookup
                            if (change.approvalGroupId && approvalGroupsData) {
                              const group = approvalGroupsData.find((g: any) => g.id === change.approvalGroupId);
                              return group?.name || change.approvalGroupId;
                            }
                            return 'Not assigned';
                          })()}
                          readOnly
                          className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Row 10: Short Description */}
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-12">
                  <div className="grid grid-cols-12 gap-4 items-start">
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1 pt-2">
                        Short Description
                      </label>
                    </div>
                    <div className="col-span-10">
                      <ResizableTextDisplay
                        content={change.shortDescription || ''}
                        defaultHeight={60}
                        minHeight={40}
                        maxHeight={300}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Reference Alert */}
              {change.ReferenceAlert && (
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-6">
                    <div className="grid grid-cols-12 gap-4 items-end">
                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Reference Alert
                        </label>
                      </div>
                      <div className="col-span-9">
                        <Link
                          to={`/alerts/${change.ReferenceAlert.id}`}
                          className="inline-flex items-center px-3 py-1 border border-blue-300 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 text-sm h-8"
                        >
                          <TicketIcon className="h-4 w-4 mr-1" />
                          {change.ReferenceAlert.number}
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Description */}
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-12">
                  <div className="grid grid-cols-12 gap-4 items-start">
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1 pt-2">
                        Description
                      </label>
                    </div>
                    <div className="col-span-10">
                      {change.description ? (
                        <ResizableTextDisplay
                          content={change.description}
                          defaultHeight={100}
                          minHeight={60}
                          maxHeight={500}
                        />
                      ) : (
                        <div className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-100 text-gray-500 text-sm italic">
                          No description provided
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Justification */}
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-12">
                  <div className="grid grid-cols-12 gap-4 items-start">
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Justification
                      </label>
                    </div>
                    <div className="col-span-10">
                      <div className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm">
                        <RichTextDisplay content={change.justification || 'Not provided'} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Risk & Impact Analysis */}
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-12">
                  <div className="grid grid-cols-12 gap-4 items-start">
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Risk & Impact Analysis
                      </label>
                    </div>
                    <div className="col-span-10">
                      <div className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm">
                        <RichTextDisplay content={change.riskImpactAnalysis || 'Not provided'} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Implementation Plan */}
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-12">
                  <div className="grid grid-cols-12 gap-4 items-start">
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Implementation Plan
                      </label>
                    </div>
                    <div className="col-span-10">
                      <div className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm">
                        <RichTextDisplay content={change.implementationPlan || 'Not provided'} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Backout Plan */}
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-12">
                  <div className="grid grid-cols-12 gap-4 items-start">
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Backout Plan
                      </label>
                    </div>
                    <div className="col-span-10">
                      <div className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm">
                        <RichTextDisplay content={change.backoutPlan || 'Not provided'} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Test Plan */}
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-12">
                  <div className="grid grid-cols-12 gap-4 items-start">
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Test Plan
                      </label>
                    </div>
                    <div className="col-span-10">
                      <div className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm">
                        <RichTextDisplay content={change.testPlan || 'Not provided'} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            )
          )}

          {/* Schedule Tab */}
          {activeTab === 'schedule' && (
            <div className="space-y-6">
              {/* Planned Start and Planned End */}
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Planned Start
                      </label>
                    </div>
                    <div className="col-span-9">
                      <input
                        type="text"
                        value={change.plannedStart ? formatDateTime(change.plannedStart) : 'Not scheduled'}
                        readOnly
                        className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                      />
                    </div>
                  </div>
                </div>
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Planned End
                      </label>
                    </div>
                    <div className="col-span-9">
                      <input
                        type="text"
                        value={change.plannedEnd ? formatDateTime(change.plannedEnd) : 'Not scheduled'}
                        readOnly
                        className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Actual Start and Actual End */}
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Actual Start
                      </label>
                    </div>
                    <div className="col-span-9">
                      <input
                        type="text"
                        value={change.actualStart ? formatDateTime(change.actualStart) : 'Not started'}
                        readOnly
                        className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                      />
                    </div>
                  </div>
                </div>
                <div className="col-span-6">
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Actual End
                      </label>
                    </div>
                    <div className="col-span-9">
                      <input
                        type="text"
                        value={change.actualEnd ? formatDateTime(change.actualEnd) : 'Not completed'}
                        readOnly
                        className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Duration Information */}
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-12">
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Schedule Information</h4>
                    
                    {/* Planned Schedule */}
                    <div className="mb-4">
                      <h5 className="text-xs font-medium text-gray-700 mb-2">Planned Schedule</h5>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <dt className="text-sm text-gray-500">Planned Start Date</dt>
                          <dd className="text-sm text-gray-900">
                            {change.plannedStart ? formatDateTime(change.plannedStart) : 'Not scheduled'}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-sm text-gray-500">Planned End Date</dt>
                          <dd className="text-sm text-gray-900">
                            {change.plannedEnd ? formatDateTime(change.plannedEnd) : 'Not scheduled'}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-sm text-gray-500">Planned Duration</dt>
                          <dd className="text-sm text-gray-900">
                            {change.plannedStart && change.plannedEnd ? (
                              (() => {
                                const start = new Date(change.plannedStart);
                                const end = new Date(change.plannedEnd);
                                const diffMs = end.getTime() - start.getTime();
                                const diffHours = Math.round(diffMs / (1000 * 60 * 60));
                                const diffDays = Math.floor(diffHours / 24);
                                const remainingHours = diffHours % 24;
                                
                                if (diffDays > 0) {
                                  return remainingHours > 0 ? `${diffDays} days, ${remainingHours} hours` : `${diffDays} days`;
                                } else {
                                  return `${diffHours} hours`;
                                }
                              })()
                            ) : 'Not calculated'}
                          </dd>
                        </div>
                      </div>
                    </div>
                    
                    {/* Actual Schedule */}
                    {(change.actualStart || change.actualEnd) && (
                      <div className="pt-4 border-t border-gray-200">
                        <h5 className="text-xs font-medium text-gray-700 mb-2">Actual Schedule</h5>
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <dt className="text-sm text-gray-500">Actual Start Date</dt>
                            <dd className="text-sm text-gray-900">
                              {change.actualStart ? formatDateTime(change.actualStart) : 'Not started'}
                            </dd>
                          </div>
                          <div>
                            <dt className="text-sm text-gray-500">Actual End Date</dt>
                            <dd className="text-sm text-gray-900">
                              {change.actualEnd ? formatDateTime(change.actualEnd) : 'Not completed'}
                            </dd>
                          </div>
                          {change.actualStart && change.actualEnd && (
                            <div>
                              <dt className="text-sm text-gray-500">Actual Duration</dt>
                              <dd className="text-sm text-gray-900">
                                {(() => {
                                  const start = new Date(change.actualStart);
                                  const end = new Date(change.actualEnd);
                                  const diffMs = end.getTime() - start.getTime();
                                  const diffHours = Math.round(diffMs / (1000 * 60 * 60));
                                  const diffDays = Math.floor(diffHours / 24);
                                  const remainingHours = diffHours % 24;
                                  
                                  if (diffDays > 0) {
                                    return remainingHours > 0 ? `${diffDays} days, ${remainingHours} hours` : `${diffDays} days`;
                                  } else {
                                    return `${diffHours} hours`;
                                  }
                                })()}
                              </dd>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                  </div>
                </div>
              </div>

              {/* Maintenance Window Information if applicable */}
              {change.maintenanceWindow && (
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-12">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-blue-900 mb-2">Maintenance Window</h4>
                      <p className="text-sm text-blue-800">
                        This change is scheduled during a maintenance window: {change.maintenanceWindow}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Approval Tab */}
          {activeTab === 'approval' && (change.cabRequired || change.changeType === 'standard') && (
            <div className="space-y-4">
              {/* Current Stage Indicator */}
              {peerReviewStatus?.currentStage && (
                <div className="bg-gray-50 rounded-lg p-4 flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Current Approval Stage</span>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    peerReviewStatus.currentStage === 'peer_review' ? 'bg-yellow-100 text-yellow-800' :
                    peerReviewStatus.currentStage === 'cab' ? 'bg-blue-100 text-blue-800' :
                    peerReviewStatus.currentStage === 'customer' ? 'bg-purple-100 text-purple-800' :
                    peerReviewStatus.currentStage === 'complete' ? 'bg-green-100 text-green-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {peerReviewStatus.currentStage ? peerReviewStatus.currentStage.replace('_', ' ').toUpperCase() : 'Not Started'}
                  </span>
                </div>
              )}

              {/* Standard Change Info Banner */}
              {change.changeType === 'standard' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <svg className="h-5 w-5 text-blue-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-blue-800">Standard Change</h3>
                      <p className="mt-1 text-sm text-blue-700">
                        Standard changes require only Peer Review approval. CAB and Customer approval stages are skipped, and the change will move directly to Scheduled state after peer review approval.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Peer Review Accordion */}
              {(peerReviewStatus?.peerReviewMembers?.length > 0 || change.peerReviewGroupId) && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setExpandedApprovalSections(prev => ({ ...prev, peerReview: !prev.peerReview }))}
                    className="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      {expandedApprovalSections.peerReview ? (
                        <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                      ) : (
                        <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                      )}
                      <UserGroupIcon className="h-5 w-5 text-blue-500" />
                      <span className="text-sm font-medium text-gray-900">Peer Review</span>
                      <span className="text-sm text-gray-500">
                        ({peerReviewStatus?.peerReviewMembers?.length || 0} members)
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      {peerReviewStatus?.peerReviewMembers?.some((m: any) => m.status === 'approved') ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Approved
                        </span>
                      ) : peerReviewStatus?.peerReviewMembers?.some((m: any) => m.status === 'rejected') ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Rejected
                        </span>
                      ) : peerReviewStatus?.currentStage === 'peer_review' ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          In Progress
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                          Pending
                        </span>
                      )}
                    </div>
                  </button>
                  {expandedApprovalSections.peerReview && peerReviewStatus?.peerReviewMembers?.length > 0 && (
                    <div className="border-t border-gray-200 bg-gray-50">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead>
                          <tr className="bg-gray-100">
                            <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                            <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                            <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Response Date</th>
                            <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {peerReviewStatus!.peerReviewMembers.map((member: any) => {
                            // Check if current user can act on this member's row
                            const isCurrentUserMember = member.userId === (user as any)?.id;
                            const canApprove = peerReviewStatus?.currentStage === 'peer_review' &&
                                              member.status === 'pending' &&
                                              (isCurrentUserMember || isUserAdminOrSuperAdmin()) &&
                                              !peerReviewStatus?.peerReviewMembers?.some((m: any) => m.status === 'approved' || m.status === 'rejected');

                            return (
                              <tr key={member.userId}>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                  {member.firstName} {member.lastName}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                  {member.email}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    member.status === 'approved' ? 'bg-green-100 text-green-800' :
                                    member.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                    member.status === 'invalidated' ? 'bg-gray-100 text-gray-600' :
                                    'bg-yellow-100 text-yellow-800'
                                  }`}>
                                    {member.status === 'approved' ? 'Approved' :
                                     member.status === 'rejected' ? 'Rejected' :
                                     member.status === 'invalidated' ? 'Not Required' :
                                     'Pending'}
                                  </span>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                  {member.respondedAt ? formatDateTime(member.respondedAt) : '-'}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                  {canApprove ? (
                                    <div className="flex space-x-2">
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          setPeerReviewApprovalData({ decision: 'approved', comments: '' });
                                          setShowPeerReviewApprovalModal(true);
                                        }}
                                        disabled={peerReviewMutation.isPending}
                                        className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                                      >
                                        Approve
                                      </button>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          setPeerReviewApprovalData({ decision: 'rejected', comments: '' });
                                          setShowPeerReviewApprovalModal(true);
                                        }}
                                        disabled={peerReviewMutation.isPending}
                                        className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                                      >
                                        Reject
                                      </button>
                                    </div>
                                  ) : member.comments ? (
                                    <div className="text-xs text-gray-500 max-w-xs">
                                      <RichTextDisplay content={member.comments} />
                                    </div>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* CAB Approval Accordion - Not shown for standard changes */}
              {change.changeType !== 'standard' && (change.approvalGroupId || approvalGroupMembersData?.length > 0) && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setExpandedApprovalSections(prev => ({ ...prev, cab: !prev.cab }))}
                    className="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      {expandedApprovalSections.cab ? (
                        <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                      ) : (
                        <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                      )}
                      <UserGroupIcon className="h-5 w-5 text-purple-500" />
                      <span className="text-sm font-medium text-gray-900">CAB Approval</span>
                      <span className="text-sm text-gray-500">
                        ({approvalGroupMembersData?.length || 0} members)
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      {hasAnyApproval() ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Approved
                        </span>
                      ) : hasAnyRejection() ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Rejected
                        </span>
                      ) : peerReviewStatus?.currentStage === 'cab' && approvalStatus.approved > 0 ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          In Progress ({approvalStatus.approved}/{approvalStatus.total})
                        </span>
                      ) : peerReviewStatus?.currentStage === 'cab' ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          Awaited
                        </span>
                      ) : peerReviewStatus?.currentStage === 'peer_review' ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                          Waiting for Peer Review
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                          Pending
                        </span>
                      )}
                    </div>
                  </button>
                  {expandedApprovalSections.cab && approvalGroupMembersData && approvalGroupMembersData.length > 0 && (
                    <div className="border-t border-gray-200 bg-gray-50">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead>
                          <tr className="bg-gray-100">
                            <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                            <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                            <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Response Date</th>
                            <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {approvalGroupMembersData.map((member: any) => {
                            // Find approval for this member (including legacy approvals with null stage)
                            const memberApproval = change.approvals?.find((a: any) => a.approverId === member.id && isCabApproval(a));

                            return (
                              <tr key={member.id}>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                  {member.firstName} {member.lastName}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                  {member.email}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  {memberApproval ? (
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                      memberApproval.state === 'approved' ? 'bg-green-100 text-green-800' :
                                      memberApproval.state === 'rejected' ? 'bg-red-100 text-red-800' :
                                      'bg-yellow-100 text-yellow-800'
                                    }`}>
                                      {memberApproval.state === 'approved' ? 'Approved' :
                                       memberApproval.state === 'rejected' ? 'Rejected' : 'Pending'}
                                    </span>
                                  ) : hasSuperAdminApproval() ? (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                      Not Required
                                    </span>
                                  ) : hasAnyApproval() || hasAnyRejection() ? (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                      Not Required
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                      Pending
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                  {memberApproval?.approvedAt ? formatDateTime(memberApproval.approvedAt) : '-'}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                  {change.cabRequired && peerReviewStatus?.currentStage === 'cab' && (!memberApproval || memberApproval.state === 'pending') && !hasAnyRejection() && !hasSuperAdminApproval() && !hasAnyApproval() &&
                                   (isUserAdminOrSuperAdmin() || member.id === (user as any)?.id) ? (
                                    <div className="flex space-x-2">
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          setApprovalModalData({decision: 'approved', comments: ''});
                                          setShowApprovalModal(true);
                                        }}
                                        disabled={approvalMutation.isPending}
                                        className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                                      >
                                        Approve
                                      </button>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          setApprovalModalData({decision: 'rejected', comments: ''});
                                          setShowApprovalModal(true);
                                        }}
                                        disabled={approvalMutation.isPending}
                                        className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                                      >
                                        Reject
                                      </button>
                                    </div>
                                  ) : memberApproval?.comments ? (
                                    <div className="text-xs text-gray-500 max-w-xs">
                                      <RichTextDisplay content={memberApproval.comments} />
                                    </div>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Customer Approval Accordion - Not shown for standard changes */}
              {change.changeType !== 'standard' && (change.customerApprovalRequested || change.customerId) && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setExpandedApprovalSections(prev => ({ ...prev, customer: !prev.customer }))}
                    className="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      {expandedApprovalSections.customer ? (
                        <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                      ) : (
                        <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                      )}
                      <UserGroupIcon className="h-5 w-5 text-indigo-500" />
                      <span className="text-sm font-medium text-gray-900">Customer Approval</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      {change.customerApprovalStatus === 'approved' || change.approvalStage === 'complete' ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Approved
                        </span>
                      ) : change.customerApprovalStatus === 'rejected' ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Rejected
                        </span>
                      ) : change.customerApprovalRequested && change.customerApprovalStatus !== 'approved' && change.customerApprovalStatus !== 'rejected' ? (
                        <div className="flex items-center space-x-2">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            Pending
                          </span>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleSendCustomerApproval(true); }}
                            className="inline-flex items-center px-2 py-1 text-xs font-medium rounded text-yellow-700 bg-yellow-50 border border-yellow-300 hover:bg-yellow-100"
                          >
                            Resend
                          </button>
                        </div>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                          Not Requested
                        </span>
                      )}
                    </div>
                  </button>
                  {expandedApprovalSections.customer && (
                    <div className="border-t border-gray-200 bg-gray-50 p-4">
                      {/* Send to Customer Button - show if not yet requested */}
                      {!change.customerApprovalRequested && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <p className="text-sm text-blue-800 mb-3">
                            {change.cabRequired
                              ? 'Once CAB approval is received, you can send this change to the customer for final approval.'
                              : 'Send this change to the customer for approval (CAB approval not required).'}
                          </p>
                          <button
                            onClick={() => handleSendCustomerApproval(false)}
                            disabled={change.cabRequired && !hasAnyApproval()}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-300 disabled:cursor-not-allowed"
                          >
                            Send to Customer for Approval
                          </button>
                          {change.cabRequired && !hasAnyApproval() && (
                            <p className="mt-2 text-xs text-gray-600">
                              CAB approval is required before sending to customer
                            </p>
                          )}
                        </div>
                      )}

                      {/* Customer Approval Status - show if requested */}
                      {change.customerApprovalRequested && (
                        <div className={`border rounded-lg p-4 ${
                          change.customerApprovalStatus === 'approved' || change.approvalStage === 'complete' ? 'bg-green-50 border-green-200' :
                          change.customerApprovalStatus === 'rejected' ? 'bg-red-50 border-red-200' :
                          'bg-yellow-50 border-yellow-200'
                        }`}>
                          <div className="flex items-start justify-between">
                            <div>
                              {(change.customerApprovalStatus === 'approved' || change.customerApprovalStatus === 'rejected' || change.approvalStage === 'complete') && change.User_Case_customerApprovedByIdToUser ? (
                                <>
                                  <p className="text-sm font-medium text-gray-900">
                                    {change.customerApprovalStatus === 'approved' || change.approvalStage === 'complete' ? 'Approved by' : 'Rejected by'}: {change.User_Case_customerApprovedByIdToUser.firstName} {change.User_Case_customerApprovedByIdToUser.lastName}
                                  </p>
                                  <p className="text-sm text-gray-500">
                                    {change.User_Case_customerApprovedByIdToUser.email}
                                  </p>
                                </>
                              ) : change.approvalStage === 'complete' ? (
                                <>
                                  <p className="text-sm font-medium text-gray-900">
                                    Customer Approval Complete
                                  </p>
                                  <p className="text-sm text-gray-500">
                                    This change has been approved by the customer.
                                  </p>
                                </>
                              ) : (
                                <>
                                  <p className="text-sm font-medium text-gray-900">
                                    {change.contactId ? 'Approval Request Sent to Contact' : 'Approval Request Sent to All Contacts'}
                                  </p>
                                  <p className="text-sm text-gray-500">
                                    {change.User_Case_contactIdToUser?.email || (change.contactId ? 'No contact email' : 'Sent to all engagement contacts')}
                                  </p>
                                </>
                              )}
                              {change.customerApprovalComments && (
                                <div className="mt-2 text-sm text-gray-600">
                                  <RichTextDisplay content={change.customerApprovalComments} />
                                </div>
                              )}
                            </div>
                            <div className="text-right">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                change.customerApprovalStatus === 'approved' || change.approvalStage === 'complete' ? 'bg-green-100 text-green-800' :
                                change.customerApprovalStatus === 'rejected' ? 'bg-red-100 text-red-800' :
                                'bg-yellow-100 text-yellow-800'
                              }`}>
                                {change.customerApprovalStatus === 'approved' || change.approvalStage === 'complete' ? 'Approved' :
                                 change.customerApprovalStatus === 'rejected' ? 'Rejected' :
                                 change.customerApprovalStatus ? change.customerApprovalStatus.charAt(0).toUpperCase() + change.customerApprovalStatus.slice(1) : 'Pending'}
                              </span>
                              {change.customerApprovedAt && (
                                <p className="mt-1 text-xs text-gray-500">
                                  {formatDateTime(change.customerApprovedAt)}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Status message */}
                          {change.customerApprovalStatus === 'pending' && change.approvalStage !== 'complete' && (
                            <div className="mt-3">
                              <p className="text-sm text-yellow-700">
                                Waiting for customer approval. The customer contact will be notified to review and approve this change.
                              </p>
                              <button
                                onClick={() => handleSendCustomerApproval(true)}
                                className="mt-3 inline-flex items-center px-3 py-1.5 border border-yellow-600 text-sm font-medium rounded-md text-yellow-700 bg-yellow-50 hover:bg-yellow-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                              >
                                Resend Customer Approval Request
                              </button>
                            </div>
                          )}
                          {change.customerApprovalStatus === 'rejected' && (
                            <div className="mt-3 text-sm text-red-700">
                              Customer has rejected this change. Please address the concerns before proceeding.
                            </div>
                          )}
                          {(change.customerApprovalStatus === 'approved' || change.approvalStage === 'complete') && (
                            <div className="mt-3 text-sm text-green-700">
                              Customer has approved this change. You can now proceed to schedule the change.
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Display approval errors */}
              {errors.approval && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <p className="text-sm text-red-800">{errors.approval}</p>
                </div>
              )}

            </div>
          )}

          {/* Notes Tab */}
          {activeTab === 'notes' && (
            <div>
              {/* Add Note Form */}
              {canAddWorkNote && (
                <form onSubmit={handleAddNote} className="mb-6">
                  <div className="mb-2">
                    <label className="block text-sm font-medium text-gray-700">Add Note</label>
                    <RichTextEditor
                      value={newNote}
                      onChange={(value) => setNewNote(value)}
                      placeholder="Enter your note..."
                      minHeight="120px"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    {!isContact() ? (
                      <div className="flex items-center space-x-4">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            value="work_note"
                            checked={noteType === 'work_note'}
                            onChange={(e) => setNoteType(e.target.value as 'work_note')}
                            className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                          />
                          <span className="text-sm text-gray-700">Work Note</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            value="customer_note"
                            checked={noteType === 'customer_note'}
                            onChange={(e) => setNoteType(e.target.value as 'customer_note')}
                            className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                          />
                          <span className="text-sm text-gray-700">Customer Note</span>
                        </label>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-600 italic">Your notes will be visible to all users</p>
                    )}

                    <button
                      type="submit"
                      disabled={addNoteMutation.isPending || !newNote.trim()}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                    >
                      {addNoteMutation.isPending ? 'Adding...' : 'Add Note'}
                    </button>
                  </div>
                </form>
              )}

              {/* Notes List */}
              <div className="space-y-4">
                {isLoadingNotes ? (
                  <TabLoadingSpinner label="Loading work notes" />
                ) : workNotes.map((note: WorkNote) => (
                  <div key={note.id} className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0">
                          <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-gray-500">
                            <span className="text-xs font-medium leading-none text-white">
                              {note.User?.firstName?.[0] || 'U'}{note.User?.lastName?.[0] || ''}
                            </span>
                          </span>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">
                            {note.User ? `${note.User.firstName} ${note.User.lastName}` : 'Unknown User'}
                          </p>
                          <div className="mt-1 text-sm text-gray-600">
                            <RichTextDisplay content={note.note} />
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 text-xs text-gray-500">
                        {note.isPublic && (
                          <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded">Public</span>
                        )}
                        <span>{formatDateTime(note.timestamp)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Closure Information Tab */}
          {activeTab === 'closure' && (
            <div className="space-y-6">
              {/* Closure Status */}
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-12">
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Change Status</h4>
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <dt className="text-sm text-gray-500">Current State</dt>
                        <dd className="text-sm text-gray-900">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            isClosedByResourceCleanup() ? 'bg-amber-100 text-amber-800' :
                            getEffectiveDisplayState() === 'closed' ? 'bg-green-100 text-green-800' :
                            change.changeState === 'cancelled' ? 'bg-red-100 text-red-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {getStateLabel(getEffectiveDisplayState())}
                          </span>
                          {isClosedByResourceCleanup() && (
                            <p className="text-xs text-amber-700 mt-1">Closed due to resource deletion</p>
                          )}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm text-gray-500">Last Updated</dt>
                        <dd className="text-sm text-gray-900">
                          {formatDateTime(change.updatedAt)}
                        </dd>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Closure Info Grid */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-100 border border-gray-300 rounded-lg">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Closed By</label>
                  <p className="text-sm text-gray-900">
                    {change.closedBy
                      ? `${change.closedBy.firstName || ''} ${change.closedBy.lastName || ''}`.trim() || '-'
                      : '-'}
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Closed On</label>
                  <p className="text-sm text-gray-900">
                    {change.closedAt ? formatDateTime(change.closedAt) : '-'}
                  </p>
                </div>
              </div>

              {/* Implementation Results */}
              {(change.changeState === 'implement' || change.changeState === 'review' || change.changeState === 'closed') && change.implementationResults && (
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-12">
                    <div className="grid grid-cols-12 gap-4 items-start">
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Implementation Results
                        </label>
                      </div>
                      <div className="col-span-10">
                        <div className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm">
                          <RichTextDisplay content={change.implementationResults || 'No implementation results recorded'} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Review Results */}
              {(change.changeState === 'review' || change.changeState === 'closed') && (
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-12">
                    <div className="grid grid-cols-12 gap-4 items-start">
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Review Results
                        </label>
                      </div>
                      <div className="col-span-10">
                        <div className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm">
                          <RichTextDisplay content={change.reviewResults || 'No review results recorded'} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Close Code */}
              {change.changeState === 'closed' && change.changeCloseCode && (
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-12">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-blue-900 mb-3">Close Code</h4>
                      <div className="flex items-center">
                        <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${
                          change.changeCloseCode === 'change_success' ? 'bg-green-100 text-green-800' :
                          change.changeCloseCode === 'change_success_with_impact' ? 'bg-green-100 text-green-800' :
                          change.changeCloseCode === 'change_failed' ? 'bg-red-100 text-red-800' :
                          change.changeCloseCode === 'change_failed_with_impact' ? 'bg-red-100 text-red-800' :
                          change.changeCloseCode === 'change_postponed' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {change.changeCloseCode === 'change_success' && 'Change Success'}
                          {change.changeCloseCode === 'change_success_with_impact' && 'Change Success With Impact'}
                          {change.changeCloseCode === 'change_failed' && 'Change Failed'}
                          {change.changeCloseCode === 'change_failed_with_impact' && 'Change Failed With Impact'}
                          {change.changeCloseCode === 'change_postponed' && 'Change Postponed'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Closure Information */}
              {(change.changeState === 'closed' || change.changeState === 'cancelled') && (
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-12">
                    <div className="grid grid-cols-12 gap-4 items-start">
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Closure Notes
                        </label>
                      </div>
                      <div className="col-span-10">
                        <div className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm">
                          <RichTextDisplay content={change.closureNotes || 'No closure notes recorded'} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Success Criteria Verification */}
              {change.changeState === 'closed' && (
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-12">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-green-900 mb-2">Success Criteria</h4>
                      <div className="space-y-2">
                        <div className="flex items-center">
                          <CheckIcon className="h-5 w-5 text-green-600 mr-2" />
                          <span className="text-sm text-green-800">Change implemented successfully</span>
                        </div>
                        {change.testPlan && (
                          <div className="flex items-center">
                            <CheckIcon className="h-5 w-5 text-green-600 mr-2" />
                            <span className="text-sm text-green-800">Test plan executed and validated</span>
                          </div>
                        )}
                        {change.backoutPlan && (
                          <div className="flex items-center">
                            <CheckIcon className="h-5 w-5 text-green-600 mr-2" />
                            <span className="text-sm text-green-800">Backout plan verified and available</span>
                          </div>
                        )}
                        <div className="flex items-center">
                          <CheckIcon className="h-5 w-5 text-green-600 mr-2" />
                          <span className="text-sm text-green-800">No unexpected issues reported</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Cancellation Reason */}
              {change.changeState === 'cancelled' && (
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-12">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-red-900 mb-2">Change Cancelled</h4>
                      <p className="text-sm text-red-800">
                        This change has been cancelled. Please refer to the work notes for cancellation reasons and any follow-up actions required.
                      </p>
                      {change.cancellationReason && (
                        <div className="mt-3">
                          <label className="block text-sm font-medium text-red-900 mb-1">Cancellation Reason</label>
                          <p className="text-sm text-red-800 bg-red-100 p-2 rounded">{change.cancellationReason}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Closure Timeline */}
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-12">
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Change Timeline</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">Created</span>
                        <span className="text-sm text-gray-900">{formatDateTime(change.createdAt)}</span>
                      </div>
                      {change.plannedStart && (
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Planned Start</span>
                          <span className="text-sm text-gray-900">{formatDateTime(change.plannedStart)}</span>
                        </div>
                      )}
                      {change.actualStart && (
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Actual Start</span>
                          <span className="text-sm text-gray-900">{formatDateTime(change.actualStart)}</span>
                        </div>
                      )}
                      {change.plannedEnd && (
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Planned End</span>
                          <span className="text-sm text-gray-900">{formatDateTime(change.plannedEnd)}</span>
                        </div>
                      )}
                      {change.actualEnd && (
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Actual End</span>
                          <span className="text-sm text-gray-900">{formatDateTime(change.actualEnd)}</span>
                        </div>
                      )}
                      <div className="flex justify-between border-t pt-2">
                        <span className="text-sm text-gray-500">Last Updated</span>
                        <span className="text-sm text-gray-900">{formatDateTime(change.updatedAt)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Attachments Tab */}
          {activeTab === 'attachments' && (
            <div className="bg-white shadow rounded-lg p-6">
              <AttachmentTab
                entityType="case"
                entityId={id!}
                canUpload={canAddAttachments}
                canDelete={canAddAttachments}
                ticketState={change?.changeState}
                onAttachmentAdded={() => {
                  // Log attachment addition
                  if (change && user) {
                    logChangeAction(
                      'Change Details - Edit',
                      'Added Attachments'
                    );
                  }
                }}
                onAttachmentDeleted={() => {
                  // Log attachment deletion
                  if (change && user) {
                    logChangeAction(
                      'Change Details - Edit',
                      'Removed Attachments'
                    );
                  }
                }}
              />
            </div>
          )}

          {/* Activities Tab */}
          {activeTab === 'activities' && (
            <div className="bg-white shadow rounded-lg p-6">
              <ActivityTab entityId={id!} />
            </div>
          )}

          {/* Related Records Tab */}
          {activeTab === 'related' && (
            <div className="bg-white shadow rounded-lg p-6">
              <RelatedRecordsTab
                caseId={id!}
                caseNumber={change?.number || ''}
                ticketType="CHANGE"
                canManage={canAddRelated}
                onRelatedRecordAdded={() => {
                  // Log related record addition
                  logChangeAction(
                    'Change Details - Edit',
                    'Added Related Record'
                  );
                }}
                onRelatedRecordDeleted={() => {
                  // Log related record deletion
                  logChangeAction(
                    'Change Details - Edit',
                    'Remove Related Record'
                  );
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Change Status Modal */}
      {pendingStateChange && (
        <ChangeStatusModal
          isOpen={!!pendingStateChange}
          onClose={handleStatusModalClose}
          onSuccess={handleStatusModalSuccess}
          changeId={id!}
          currentState={change?.changeState || 'new'}
          targetState={pendingStateChange.targetState}
          actionLabel={pendingStateChange.actionLabel}
          currentChange={change}
        />
      )}

      {/* Authorize Modal removed - state transition to 'authorize' now happens automatically after CAB approval */}

      {/* Assign Modal - Update Assignment Group and Assigned To */}
      <Transition appear show={showAssignModal} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={handleAssignModalClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900 flex items-center justify-between"
                  >
                    Assign Change
                    <button
                      type="button"
                      className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      onClick={handleAssignModalClose}
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </Dialog.Title>

                  <div className="mt-4 space-y-4">
                    {/* Assignment Group Dropdown */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Assignment Group <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={assignForm.assignmentGroup}
                        onChange={(e) => {
                          setAssignForm({
                            ...assignForm,
                            assignmentGroup: e.target.value,
                            assignedTo: '' // Reset assigned to when group changes
                          });
                        }}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        required
                      >
                        <option value="">Select Assignment Group</option>
                        {assignmentGroupsData?.map((group: any) => (
                          <option key={group.id} value={group.id}>
                            {group.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Assigned To Dropdown */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Assigned To
                      </label>
                      <select
                        value={assignForm.assignedTo}
                        onChange={(e) => setAssignForm({ ...assignForm, assignedTo: e.target.value })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        disabled={!assignForm.assignmentGroup}
                      >
                        <option value="">
                          {!assignForm.assignmentGroup
                            ? 'Select Assignment Group first'
                            : assignGroupMembersData?.length > 0
                            ? 'Select Assigned To'
                            : 'No members in this group'}
                        </option>
                        {assignGroupMembersData?.map((member: any) => (
                          <option key={member.id} value={member.id}>
                            {member.firstName} {member.lastName} ({member.email})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end gap-3">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      onClick={handleAssignModalClose}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:bg-gray-300 disabled:cursor-not-allowed"
                      onClick={handleAssignSubmit}
                      disabled={!assignForm.assignmentGroup || !assignForm.assignedTo}
                    >
                      Assign
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* CAB Approval Modal */}
      <Transition appear show={showApprovalModal} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => setShowApprovalModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900 flex items-center justify-between"
                  >
                    {approvalModalData.decision === 'approved' ? 'Approve Change' : 'Reject Change'}
                    <button
                      type="button"
                      className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      onClick={() => setShowApprovalModal(false)}
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </Dialog.Title>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700">
                      {approvalModalData.decision === 'approved' ? 'Comments (Optional)' : 'Rejection Reason'}
                      {approvalModalData.decision === 'rejected' && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    <RichTextEditor
                      value={approvalModalData.comments}
                      onChange={(value) => setApprovalModalData({...approvalModalData, comments: value})}
                      placeholder={approvalModalData.decision === 'approved'
                        ? 'Add any approval comments...'
                        : 'Please provide a reason for rejection...'}
                      minHeight="120px"
                    />
                  </div>

                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      onClick={() => setShowApprovalModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className={`inline-flex justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 ${
                        approvalModalData.decision === 'approved'
                          ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                          : 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                      }`}
                      disabled={approvalMutation.isPending || (approvalModalData.decision === 'rejected' && !approvalModalData.comments.trim())}
                      onClick={() => {
                        approvalMutation.mutate({ 
                          decision: approvalModalData.decision, 
                          comments: approvalModalData.comments || undefined 
                        });
                        setShowApprovalModal(false);
                        setApprovalModalData({decision: 'approved', comments: ''});
                      }}
                    >
                      {approvalMutation.isPending ? 'Processing...' : 
                       approvalModalData.decision === 'approved' ? 'Approve' : 'Reject'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Peer Review Approval Modal */}
      <Transition appear show={showPeerReviewApprovalModal} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => setShowPeerReviewApprovalModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900 flex items-center justify-between"
                  >
                    {peerReviewApprovalData.decision === 'approved' ? 'Approve Peer Review' : 'Reject Peer Review'}
                    <button
                      type="button"
                      className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      onClick={() => setShowPeerReviewApprovalModal(false)}
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </Dialog.Title>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700">
                      {peerReviewApprovalData.decision === 'approved' ? 'Approval Notes' : 'Rejection Notes'}
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <RichTextEditor
                      value={peerReviewApprovalData.comments}
                      onChange={(value) => setPeerReviewApprovalData({...peerReviewApprovalData, comments: value})}
                      placeholder={peerReviewApprovalData.decision === 'approved'
                        ? 'Please provide your approval notes...'
                        : 'Please provide a reason for rejection...'}
                      minHeight="120px"
                    />
                  </div>

                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      onClick={() => setShowPeerReviewApprovalModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className={`inline-flex justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 ${
                        peerReviewApprovalData.decision === 'approved'
                          ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                          : 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                      }`}
                      disabled={peerReviewMutation.isPending || !peerReviewApprovalData.comments.trim()}
                      onClick={() => {
                        peerReviewMutation.mutate({
                          decision: peerReviewApprovalData.decision,
                          comments: peerReviewApprovalData.comments
                        });
                      }}
                    >
                      {peerReviewMutation.isPending ? 'Processing...' :
                       peerReviewApprovalData.decision === 'approved' ? 'Approve' : 'Reject'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Rejection Reason Modal */}
      <Transition appear show={showRejectionModal} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => setShowRejectionModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900 flex items-center justify-between"
                  >
                    <span className="flex items-center">
                      <ExclamationTriangleIcon className="h-6 w-6 text-red-500 mr-2" />
                      Reject Change Request
                    </span>
                    <button
                      type="button"
                      className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      onClick={() => {
                        setShowRejectionModal(false);
                        setRejectionReason('');
                        setErrors({});
                      }}
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </Dialog.Title>

                  <div className="mt-4">
                    <p className="text-sm text-gray-500 mb-4">
                      Please provide a detailed reason for rejecting this change request. This will help the team understand what needs to be addressed.
                    </p>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Rejection Reason <span className="text-red-500">*</span>
                    </label>
                    <RichTextEditor
                      value={rejectionReason}
                      onChange={(value) => {
                        setRejectionReason(value);
                        setErrors({});
                      }}
                      placeholder="Explain why you are rejecting this change request..."
                      minHeight="120px"
                      className={errors.rejection ? 'border-red-300' : ''}
                    />
                    {errors.rejection && (
                      <p className="mt-2 text-sm text-red-600">{errors.rejection}</p>
                    )}
                  </div>

                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      onClick={() => {
                        setShowRejectionModal(false);
                        setRejectionReason('');
                        setErrors({});
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50"
                      disabled={!rejectionReason.trim() || approvalMutation.isPending}
                      onClick={handleRejectionSubmit}
                    >
                      {approvalMutation.isPending ? 'Processing...' : 'Submit Rejection'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* CAB Approval Notes Modal */}
      <Transition appear show={showCabApprovalModal} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => setShowCabApprovalModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900 flex items-center justify-between"
                  >
                    <span className="flex items-center">
                      <CheckCircleIcon className="h-6 w-6 text-green-500 mr-2" />
                      Approve Change Request
                    </span>
                    <button
                      type="button"
                      className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      onClick={() => {
                        setShowCabApprovalModal(false);
                        setCabApprovalNotes('');
                        setErrors({});
                      }}
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </Dialog.Title>

                  <div className="mt-4">
                    <p className="text-sm text-gray-500 mb-4">
                      Please provide notes for approving this change request. This will help document the approval decision.
                    </p>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Approval Notes <span className="text-red-500">*</span>
                    </label>
                    <RichTextEditor
                      value={cabApprovalNotes}
                      onChange={(value) => {
                        setCabApprovalNotes(value);
                        setErrors({});
                      }}
                      placeholder="Explain why you are approving this change request..."
                      minHeight="120px"
                      className={errors.approval ? 'border-red-300' : ''}
                    />
                    {errors.approval && (
                      <p className="mt-2 text-sm text-red-600">{errors.approval}</p>
                    )}
                  </div>

                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      onClick={() => {
                        setShowCabApprovalModal(false);
                        setCabApprovalNotes('');
                        setErrors({});
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50"
                      disabled={!cabApprovalNotes.trim() || approvalMutation.isPending}
                      onClick={handleCabApprovalSubmit}
                    >
                      {approvalMutation.isPending ? 'Processing...' : 'Submit Approval'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Customer Approval Request Result Modal */}
      <Transition appear show={showCustomerApprovalModal} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => setShowCustomerApprovalModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900 flex items-center justify-between"
                  >
                    <span className="flex items-center">
                      {customerApprovalMessage.type === 'success' ? (
                        <CheckIcon className="h-6 w-6 text-green-500 mr-2" />
                      ) : (
                        <ExclamationTriangleIcon className="h-6 w-6 text-red-500 mr-2" />
                      )}
                      {customerApprovalMessage.type === 'success' ? 'Request Sent' : 'Request Failed'}
                    </span>
                    <button
                      type="button"
                      className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      onClick={() => setShowCustomerApprovalModal(false)}
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </Dialog.Title>

                  <div className="mt-4">
                    <div className={`p-4 rounded-md ${
                      customerApprovalMessage.type === 'success'
                        ? 'bg-green-50 border border-green-200'
                        : 'bg-red-50 border border-red-200'
                    }`}>
                      <p className={`text-sm ${
                        customerApprovalMessage.type === 'success' ? 'text-green-800' : 'text-red-800'
                      }`}>
                        {customerApprovalMessage.message}
                      </p>
                    </div>

                    {customerApprovalMessage.type === 'success' && (
                      <div className="mt-4 text-sm text-gray-600">
                        <p>The customer will receive an email with:</p>
                        <ul className="list-disc list-inside mt-2 space-y-1">
                          <li>Change request details</li>
                          <li>Approve and Reject buttons</li>
                          <li>Direct links to respond</li>
                        </ul>
                      </div>
                    )}
                  </div>

                  <div className="mt-6 flex justify-end">
                    <button
                      type="button"
                      className={`inline-flex justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                        customerApprovalMessage.type === 'success'
                          ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                          : 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                      }`}
                      onClick={() => setShowCustomerApprovalModal(false)}
                    >
                      OK
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Duplicate Change Modal */}
      <DuplicateChangeModal
        isOpen={duplicateModalOpen}
        onClose={() => setDuplicateModalOpen(false)}
        change={change}
        onSuccess={(duplicatedChange: any) => {
          logChangeAction(
            'Change Details - Change Created',
            'Duplicate Change',
            {},
            duplicatedChange
          );
        }}
      />

      {/* Create Service Request from Change Modal */}
      <CreateServiceRequestFromChangeModal
        isOpen={createServiceRequestModalOpen}
        onClose={() => setCreateServiceRequestModalOpen(false)}
        change={change}
        onSuccess={(serviceRequest: any) => {
          logChangeAction(
            'Change Details - Service Request Created',
            'Created Service Request',
            {
              serviceRequestNumber: serviceRequest?.number || serviceRequest?.id || 'Unknown',
            }
          );
        }}
      />

      {/* Record Time Modal */}
      {change && (
        <RecordTimeModal
          isOpen={recordTimeModalOpen}
          onClose={() => setRecordTimeModalOpen(false)}
          onSuccess={() => {
            // Optionally refresh the change data after recording time
            queryClient.invalidateQueries({ queryKey: ['change', id] });
          }}
          ticketNumber={change.changeNumber || change.number || ''}
          customerName={change.Customer?.accountName || ''}
          customerId={change.customerId || change.Customer?.id || ''}
          engagementName={change.Engagement?.name || ''}
          engagementId={change.engagementId || change.Engagement?.id || ''}
          groupName={change.AssignmentGroup?.name || change.Group_Case_assignmentGroupIdToGroup?.name || ''}
          groupId={change.assignmentGroupId || change.AssignmentGroup?.id || change.Group_Case_assignmentGroupIdToGroup?.id || ''}
        />
      )}

      {/* Assignment Group Change Confirmation Modal */}
      <AssignmentGroupChangeConfirmModal
        isOpen={groupChangeConfirmModal.isOpen}
        onConfirm={handleConfirmGroupChange}
        onCancel={handleCancelGroupChange}
        newGroupName={groupChangeConfirmModal.newGroupName}
        ticketNumber={change?.number}
      />

      {/* Re-Initiate Modal */}
      <Transition appear show={reInitiateModal.isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => setReInitiateModal({ isOpen: false, plannedStart: '', plannedEnd: '' })}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                  <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                    <div className="sm:flex sm:items-start">
                      <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                        <ArrowPathIcon className="h-6 w-6 text-blue-600" />
                      </div>
                      <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left flex-1">
                        <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                          Re-Initiate Change
                        </Dialog.Title>
                        <div className="mt-4 space-y-4">
                          <div>
                            <label htmlFor="plannedStart" className="block text-sm font-medium text-gray-700 mb-1">
                              Planned Start Date
                            </label>
                            <DateTimePickerInput
                              id="plannedStart"
                              value={reInitiateModal.plannedStart}
                              onChange={(value) => setReInitiateModal(prev => ({ ...prev, plannedStart: value }))}
                            />
                          </div>
                          <div>
                            <label htmlFor="plannedEnd" className="block text-sm font-medium text-gray-700 mb-1">
                              Planned End Date
                            </label>
                            <DateTimePickerInput
                              id="plannedEnd"
                              value={reInitiateModal.plannedEnd}
                              onChange={(value) => setReInitiateModal(prev => ({ ...prev, plannedEnd: value }))}
                              min={reInitiateModal.plannedStart || undefined}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                    <button
                      type="button"
                      disabled={reInitiateMutation.isPending}
                      onClick={() => {
                        reInitiateMutation.mutate({
                          plannedStart: reInitiateModal.plannedStart ? datetimeLocalToUTC(reInitiateModal.plannedStart, preferences.timezone) : undefined,
                          plannedEnd: reInitiateModal.plannedEnd ? datetimeLocalToUTC(reInitiateModal.plannedEnd, preferences.timezone) : undefined
                        });
                      }}
                      className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 sm:ml-3 sm:w-auto disabled:opacity-50"
                    >
                      {reInitiateMutation.isPending ? 'Re-Initiating...' : 'Re-Initiate'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setReInitiateModal({ isOpen: false, plannedStart: '', plannedEnd: '' })}
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                    >
                      Cancel
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Customer Approval Response Modal */}
      <Transition appear show={showCustomerApprovalResponseModal} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => setShowCustomerApprovalResponseModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                  <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                    <div className="sm:flex sm:items-start">
                      <div className={`mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 ${
                        customerApprovalDecision === 'approved' ? 'bg-green-100' : 'bg-red-100'
                      }`}>
                        {customerApprovalDecision === 'approved' ? (
                          <CheckIcon className="h-6 w-6 text-green-600" />
                        ) : (
                          <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                          </svg>
                        )}
                      </div>
                      <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left flex-1">
                        <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                          {customerApprovalDecision === 'approved' ? 'Approve Change Request' : 'Reject Change Request'}
                        </Dialog.Title>
                        <div className="mt-4">
                          <label htmlFor="customer-approval-comments" className="block text-sm font-medium text-gray-700 mb-2">
                            Comments {customerApprovalDecision === 'rejected' && <span className="text-red-600">*</span>}
                          </label>
                          <RichTextEditor
                            value={customerApprovalComments}
                            onChange={(value) => setCustomerApprovalComments(value)}
                            placeholder={customerApprovalDecision === 'approved' ? 'Add optional comments...' : 'Please explain why you are rejecting this change...'}
                            minHeight="120px"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                    <button
                      type="button"
                      disabled={customerApprovalDecision === 'rejected' && !customerApprovalComments.trim()}
                      className={`inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm sm:ml-3 sm:w-auto ${
                        customerApprovalDecision === 'approved'
                          ? 'bg-green-600 hover:bg-green-500 disabled:bg-green-300'
                          : 'bg-red-600 hover:bg-red-500 disabled:bg-red-300'
                      } disabled:cursor-not-allowed`}
                      onClick={() => {
                        customerApprovalMutation.mutate({
                          decision: customerApprovalDecision,
                          comments: customerApprovalComments
                        });
                      }}
                    >
                      {customerApprovalDecision === 'approved' ? 'Confirm Approval' : 'Confirm Rejection'}
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                      onClick={() => {
                        setShowCustomerApprovalResponseModal(false);
                        setCustomerApprovalComments('');
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Close Change Modal */}
      <Transition appear show={showCloseModal} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => setShowCloseModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="w-full max-w-4xl transform overflow-visible rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="flex items-center justify-between mb-4">
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                      Close Change Request
                    </Dialog.Title>
                    <button
                      type="button"
                      className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      onClick={() => {
                        setShowCloseModal(false);
                        setCloseForm({ closeCode: '', closureNotes: '', actualStartDate: '', actualEndDate: '' });
                      }}
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>
                  {/* Warning banner when closing from Scheduled state */}
                  {change?.changeState === 'scheduled' && (
                    <div className="mb-4 rounded-md bg-yellow-50 border border-yellow-200 p-3">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" aria-hidden="true" />
                        </div>
                        <div className="ml-3">
                          <p className="text-sm text-yellow-800">
                            <span className="font-medium">Note:</span> Implementation step is being skipped. This change will be closed directly from the Scheduled state.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="space-y-6">
                    <div>
                      <label htmlFor="close-code" className="block text-sm font-medium text-gray-700 mb-2">
                        Close Code <span className="text-red-600">*</span>
                      </label>
                      <select
                        id="close-code"
                        value={closeForm.closeCode}
                        onChange={(e) => setCloseForm(prev => ({ ...prev, closeCode: e.target.value }))}
                        className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="">Select a close code...</option>
                        {getAvailableCloseCodes().map((code) => (
                          <option key={code.value} value={code.value}>
                            {code.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="actual-start-date" className="block text-sm font-medium text-gray-700 mb-2">
                          Actual Start Date <span className="text-red-600">*</span>
                        </label>
                        <DateTimePickerInput
                          id="actual-start-date"
                          value={closeForm.actualStartDate}
                          onChange={(value) => {
                            console.log('[CloseChangeModal] actualStartDate onChange:', value);
                            setCloseForm(prev => ({ ...prev, actualStartDate: value }));
                          }}
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="actual-end-date" className="block text-sm font-medium text-gray-700 mb-2">
                          Actual End Date <span className="text-red-600">*</span>
                        </label>
                        <DateTimePickerInput
                          id="actual-end-date"
                          value={closeForm.actualEndDate}
                          onChange={(value) => setCloseForm(prev => ({ ...prev, actualEndDate: value }))}
                          min={closeForm.actualStartDate || undefined}
                          error={!!(closeForm.actualStartDate && closeForm.actualEndDate && new Date(closeForm.actualEndDate) <= new Date(closeForm.actualStartDate))}
                          required
                        />
                        {closeForm.actualStartDate && closeForm.actualEndDate && new Date(closeForm.actualEndDate) <= new Date(closeForm.actualStartDate) && (
                          <p className="mt-1 text-sm text-red-600">End date must be greater than start date</p>
                        )}
                      </div>
                    </div>
                    <div>
                      <label htmlFor="closure-notes" className="block text-sm font-medium text-gray-700 mb-2">
                        Closure Notes
                      </label>
                      <RichTextEditor
                        value={closeForm.closureNotes}
                        onChange={(value) => setCloseForm(prev => ({ ...prev, closureNotes: value }))}
                        placeholder="Add any additional closure notes..."
                        minHeight="120px"
                      />
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      type="button"
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      onClick={() => {
                        setShowCloseModal(false);
                        setCloseForm({ closeCode: '', closureNotes: '', actualStartDate: '', actualEndDate: '' });
                      }}
                      disabled={closeChangeMutation.isPending}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      disabled={
                        !closeForm.closeCode ||
                        !closeForm.actualStartDate ||
                        !closeForm.actualEndDate ||
                        (closeForm.actualStartDate && closeForm.actualEndDate && new Date(closeForm.actualEndDate) <= new Date(closeForm.actualStartDate)) ||
                        closeChangeMutation.isPending
                      }
                      className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                      onClick={handleCloseSubmit}
                    >
                      {closeChangeMutation.isPending ? 'Closing...' : 'Close Change'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Breach Reason Modal */}
      <BreachReasonModal
        isOpen={breachReasonModal.isOpen}
        onClose={() => setBreachReasonModal({ isOpen: false, targetType: undefined })}
        onConfirm={(reason, targetType) => {
          if (targetType) {
            breachReasonMutation.mutate({ breachReason: reason, targetType });
          }
        }}
        targetType={breachReasonModal.targetType}
        isLoading={breachReasonMutation.isPending}
      />

    </div>
  );
};

export default ChangeDetail;
